import { useState, useEffect, useRef, useCallback, lazy, Suspense } from "react";
import { api, getToken } from "./lib/api";
import { installProductionAuth } from "./lib/productionAuth.js";

/** When false, Learn · Topics / Learn · K8s are hidden from nav; knowledge still answers in Chat. */
const SHOW_LEARN_UI = false;

const Chat = lazy(() => import("./components/Chat.jsx").then((m) => ({ default: m.Chat })));
const LogsExplorer = lazy(() => import("./components/LogsExplorer.jsx").then((m) => ({ default: m.LogsExplorer })));
const ItsmIncidents = lazy(() => import("./components/ItsmIncidents.jsx").then((m) => ({ default: m.ItsmIncidents })));
const ItsmChanges = lazy(() => import("./components/ItsmChanges.jsx").then((m) => ({ default: m.ItsmChanges })));
const ItsmCmdb = lazy(() => import("./components/ItsmCmdb.jsx").then((m) => ({ default: m.ItsmCmdb })));
const Integrations = lazy(() => import("./components/Integrations.jsx").then((m) => ({ default: m.Integrations })));
const PlatformArchitecture = lazy(() => import("./components/PlatformArchitecture.jsx").then((m) => ({ default: m.PlatformArchitecture })));
const IntelliRagIT = lazy(() => import("./components/IntelliRagIT.jsx").then((m) => ({ default: m.IntelliRagIT })));
const IntelliRagTutor = lazy(() => import("./components/IntelliRagTutor.jsx").then((m) => ({ default: m.IntelliRagTutor })));
const DashboardView = lazy(() => import("./components/Dashboard.jsx").then((m) => ({ default: m.Dashboard })));
const TopicsLearn = lazy(() => import("./components/TopicsLearn.jsx").then((m) => ({ default: m.TopicsLearn })));
const KubernetesLearn = lazy(() => import("./components/KubernetesLearn.jsx").then((m) => ({ default: m.KubernetesLearn })));
import { SignInPage, SignUpPage, hasSession, clearSession } from "./components/AuthGate";
import { LandingPage } from "./components/LandingPage";
import { KnowledgeUploadBar, UPLOAD_TYPES_LABEL } from "./components/KnowledgeUploadBar";
import { PageShell } from "./components/PageShell";
import { getRoute, getAppPage, navigate as goRoute, APP_PAGE_IDS, appPageHash } from "./lib/routes";
import { isProductionConsole } from "./lib/appMode.js";
import { getItem, removeItem, setItem } from "./lib/safeStorage";
import { readLogsDomain, persistLogsDomain } from "./lib/mockLogs";
import { countOpenIncidents } from "./lib/mockItsm";

/** Bump when global CSS changes so cached SPAs refresh injected styles. */
const GLOBAL_STYLE_VERSION = "20260728-prod-ux-v4";

const STYLES = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    /* Claude Desktop–inspired warm neutral + terracotta accent (Wecrew Copilot) */
    --bg:       #f7f7f4;
    --bg-2:     #eeeee8;
    --surface:  #fafaf8;
    --card:     #ffffff;
    --paper-warm: #fafaf8;
    --border:   rgba(28,28,28,0.08);
    --border2:  rgba(28,28,28,0.14);
    --primary:  #c6613f;
    --primary2: #a84f32;
    --primaryL: #f5ebe6;
    --accent:   #0f7a55;
    --accentL:  #e8f5ef;
    --accent-2: #5c5c5c;
    --gold:     #8a6d2e;
    --text:     #1c1c1c;
    --text2:    #5c5c5c;
    --muted:    #9a9a9a;
    --success:  #0f7a55;
    --successL: #e8f5ef;
    --warning:  #8a6d2e;
    --warningL: #f5efe0;
    --danger:   #dc2626;
    --dangerL:  #FEE2E2;
    --purple:   #6b5b95;
    --purpleL:  #f3f0f8;
    --plum:     #7c3aed;
    --plumL:    #f3e8ff;
    --amber:    #c6613f;
    --amberL:   #f5ebe6;
    --ink:      #1c1c1c;
    --paper:    #fafaf8;
    --sidebar-w: 260px;
    --sidebar-bg: #ecece7;
    --sidebar-surface: #e4e4de;
    --sidebar-border: rgba(0,0,0,0.06);
    --sidebar-text: #1c1c1c;
    --sidebar-muted: #6b6b6b;
    --sidebar-hover: rgba(0,0,0,0.05);
    --sidebar-active-bg: #dcdcd4;
    --top-h:    52px;
    --radius:   12px;
    --radius2:  16px;
    --shadow:   0 1px 2px rgba(0,0,0,0.04);
    --shadow2:  0 8px 30px rgba(0,0,0,0.08);
    --display:  'Instrument Serif', 'Source Serif 4', Georgia, serif;
    --serif:    'Source Serif 4', Georgia, 'Times New Roman', serif;
    --sans:     'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif;
    --ui-display: 'Outfit', 'Inter', system-ui, sans-serif;
    --mono-f:   'JetBrains Mono', ui-monospace, monospace;
    --chat-col:  min(720px, 100%);
  }
  html, body, #root {
    height: 100%;
    background: var(--paper-warm);
    background-color: var(--bg);
    color: var(--text);
    font-family: var(--sans);
    font-size: 14px;
    -webkit-font-smoothing: antialiased;
  }
  ::-webkit-scrollbar { width: 4px; height: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: var(--border2); border-radius: 4px; }
  button { cursor: pointer; border: none; background: none; color: inherit; font-family: inherit; }
  input, textarea, select { font-family: inherit; color: inherit; background: none; border: none; outline: none; }
  a { color: inherit; text-decoration: none; }
  .outfit { font-family: var(--display); font-weight: 400; letter-spacing: -0.01em; }
  .mono   { font-family: var(--mono-f); }

  /* ── Layout ── */
  .shell {
    display: flex; height: 100vh; overflow: hidden;
    --sidebar-bg: #ecece7;
    --sidebar-surface: #e4e4de;
    --sidebar-border: rgba(0,0,0,0.06);
    --sidebar-text: #1c1c1c;
    --sidebar-muted: #6b6b6b;
    --sidebar-hover: rgba(0,0,0,0.05);
    --sidebar-active-bg: #dcdcd4;
    background: var(--bg);
  }

  /* ── Sidebar (light console — explicit colors so stale :root caches cannot force dark) ── */
  .shell aside.sidebar,
  .shell > aside.sidebar {
    background-color: #ecece7 !important;
    background-image: none !important;
    color: #1c1c1c !important;
    border-right-color: rgba(0,0,0,0.06) !important;
  }
  .shell aside.sidebar .sidebar-footer {
    background-color: #e4e4de !important;
  }
  .shell aside.sidebar .nav-item.active {
    background-color: #dcdcd4 !important;
    color: #1c1c1c !important;
    border-color: transparent !important;
    box-shadow: inset 0 0 0 1px rgba(0,0,0,0.07) !important;
  }
  .sidebar {
    width: var(--sidebar-w); flex-shrink: 0;
    background: #ecece7;
    background: var(--sidebar-bg, #ecece7);
    border-right: 1px solid var(--sidebar-border, rgba(0,0,0,0.06));
    color: var(--sidebar-text, #1c1c1c);
    display: flex; flex-direction: column;
    overflow: hidden; z-index: 200;
    transition: transform 0.28s cubic-bezier(.4,0,.2,1), box-shadow 0.28s;
  }
  @media (max-width: 768px) {
    .sidebar {
      position: fixed; top: 0; left: 0; bottom: 0;
      transform: translateX(-100%);
      box-shadow: none;
    }
    .sidebar.open {
      transform: translateX(0);
      box-shadow: 4px 0 32px rgba(0,0,0,0.15);
    }
    .overlay {
      position: fixed; inset: 0; background: rgba(0,0,0,0.35); z-index: 190;
      animation: fadeIn 0.2s ease;
    }
    @keyframes fadeIn { from{opacity:0} to{opacity:1} }
  }
  .sidebar-logo {
    padding: 14px 18px;
    display: flex; align-items: center; gap: 10px;
    border-bottom: 1px solid var(--sidebar-border);
    flex-shrink: 0;
  }
  .logo-mark {
    width: 34px; height: 34px; border-radius: 10px;
    background: var(--primary);
    display: grid; place-items: center; position: relative;
    font-size: 17px; font-style: italic; color: #fff;
    font-family: var(--display); flex-shrink: 0;
    box-shadow: none;
  }
  .logo-mark::after { display: none; }
  .logo-mark span { position: relative; z-index: 2; }
  .logo-text { font-family: var(--display); font-weight: 400; font-size: 17px; letter-spacing: 0.2px; color: var(--sidebar-text); }
  .logo-text em { font-style: italic; color: var(--primary); }
  .logo-badge { font-size: 9px; font-weight: 600; font-family: var(--mono-f); letter-spacing: 0.5px; text-transform: uppercase; background: var(--primaryL); color: var(--primary); padding: 2px 6px; border-radius: 5px; margin-left: 4px; border: 1px solid rgba(198,97,63,0.25); }
  .sidebar-close { margin-left: auto; display: none; font-size: 18px; color: var(--sidebar-muted); padding: 4px; }
  @media (max-width: 768px) { .sidebar-close { display: block; } }
  .sidebar-nav { flex: 1; overflow-y: auto; padding: 8px 0; }
  .nav-section { padding: 14px 16px 6px; font-size: 11px; font-weight: 600; letter-spacing: 0.02em; color: var(--sidebar-muted); }
  .nav-item {
    display: flex; align-items: center; gap: 10px;
    padding: 8px 12px; margin: 1px 8px; border-radius: 8px;
    cursor: pointer; font-size: 13.5px; font-weight: 500; color: var(--sidebar-muted);
    transition: all 0.15s; border: 1px solid transparent;
  }
  .nav-item:hover { background: var(--sidebar-hover); color: var(--sidebar-text); }
  .nav-item.active {
    background: rgba(0, 0, 0, 0.07);
    background: var(--sidebar-active-bg, rgba(0, 0, 0, 0.07));
    color: var(--sidebar-text, #1c1c1c);
    font-weight: 600;
    border-color: transparent;
    box-shadow: none;
  }
  .nav-item.active .nav-icon { filter: none; }
  .nav-icon { font-size: 16px; width: 20px; text-align: center; flex-shrink: 0; }
  .nav-badge { margin-left: auto; background: var(--primary); color: #fff; font-size: 10px; font-weight: 700; padding: 1px 6px; border-radius: 10px; }
  .nav-badge.red { background: var(--danger); }
  .sidebar-footer {
    padding: 12px;
    border-top: 1px solid var(--sidebar-border);
    display: flex; align-items: center; gap: 10px;
    cursor: pointer; flex-shrink: 0;
    background: var(--sidebar-surface);
  }
  .sidebar-footer:hover { background: var(--sidebar-hover); }
  .sidebar-signout-btn {
    margin-left: auto; padding: 6px 10px; border-radius: 8px;
    font-size: 11px; font-weight: 700; color: var(--danger);
    border: 1.5px solid #FECACA; background: var(--dangerL); flex-shrink: 0;
  }
  .sidebar-footer:hover .sidebar-signout-btn { background: #FEE2E2; }
  .avatar { width: 34px; height: 34px; border-radius: 50%; background: linear-gradient(135deg, var(--primary), var(--purple)); display: flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 700; color: #fff; flex-shrink: 0; }
  .user-name { font-size: 13px; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; color: var(--sidebar-text); }
  .user-role { font-size: 11px; color: var(--sidebar-muted); }

  /* ── Topbar ── */
  .topbar {
    height: var(--top-h); flex-shrink: 0;
    background: var(--surface);
    border-bottom: 1px solid var(--border);
    display: flex; align-items: center; gap: 10px;
    padding: 0 20px;
    box-shadow: none;
  }
  .topbar-chat { background: var(--bg); border-bottom-color: transparent; }
  .topbar-chat .topbar-search { display: none; }
  .topbar-chat .topbar-title { text-align: center; font-family: var(--sans); font-weight: 600; font-size: 14px; color: var(--text2); }
  .hamburger { display: none; width: 36px; height: 36px; border-radius: 8px; align-items: center; justify-content: center; font-size: 20px; color: var(--text2); flex-shrink: 0; }
  @media (max-width: 768px) { .hamburger { display: flex; } }
  .topbar-title { font-family: var(--display); font-weight: 700; font-size: 16px; flex: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; color: var(--text); }
  .topbar-route { font-size: 10px; color: var(--muted); letter-spacing: 0.02em; margin-top: 2px; }
  .topbar-title-block { flex: 1; min-width: 0; display: flex; flex-direction: column; justify-content: center; }
  .topbar-search {
    display: flex; align-items: center; gap: 8px;
    background: var(--bg); border: 1.5px solid var(--border);
    border-radius: 9px; padding: 0 12px; height: 36px; width: 220px;
    transition: border-color 0.15s;
  }
  .topbar-search:focus-within { border-color: var(--primary); }
  .topbar-search input { flex: 1; font-size: 13px; color: var(--text); background: transparent; }
  .topbar-search input::placeholder { color: var(--muted); }
  @media (max-width: 600px) { .topbar-search { display: none; } }
  .topbar-btn { width: 36px; height: 36px; border-radius: 9px; display: flex; align-items: center; justify-content: center; background: var(--bg); border: 1.5px solid var(--border); font-size: 16px; color: var(--text2); transition: all 0.15s; position: relative; flex-shrink: 0; }
  .topbar-btn:hover { border-color: var(--primary); color: var(--primary); background: var(--primaryL); }
  .notif-dot { position: absolute; top: 5px; right: 5px; width: 7px; height: 7px; background: var(--danger); border-radius: 50%; border: 2px solid var(--surface); }
  .user-menu-wrap { position: relative; flex-shrink: 0; }
  .user-menu {
    position: absolute; top: calc(100% + 8px); right: 0; min-width: 188px;
    background: var(--surface); border: 1.5px solid var(--border); border-radius: 12px;
    box-shadow: 0 16px 40px rgba(14,17,22,.12); padding: 6px; z-index: 300;
    animation: fadeIn 0.15s ease;
  }
  .user-menu-head { padding: 10px 12px 8px; border-bottom: 1px solid var(--border); margin-bottom: 4px; }
  .user-menu-name { font-size: 13px; font-weight: 700; color: var(--text); }
  .user-menu-sub { font-size: 11px; color: var(--muted); margin-top: 2px; }
  .user-menu-item {
    width: 100%; display: flex; align-items: center; gap: 8px;
    padding: 9px 12px; border-radius: 8px; font-size: 13px; font-weight: 600;
    color: var(--text2); text-align: left; transition: all 0.15s;
  }
  .user-menu-item:hover { background: var(--bg); color: var(--text); }
  .user-menu-item.danger { color: var(--danger); }
  .user-menu-item.danger:hover { background: var(--dangerL); }

  /* ── Main ── */
  .main {
    flex: 1; display: flex; flex-direction: column; min-width: 0; overflow: hidden;
    position: relative;
    background: var(--bg);
  }
  .main::before { display: none; }
  .main > * { position: relative; z-index: 1; }
  .content { flex: 1; overflow-y: auto; padding: 20px; background: transparent; }
  .content:has(.chat-shell) { padding: 0; overflow: hidden; }
  .content:has(.ir-it-root), .content:has(.ir-tutor-root) { padding: 0; overflow: hidden; }
  @media (max-width: 600px) { .content { padding: 14px; } .content:has(.chat-shell) { padding: 0; } }

  /* ── Cards ── */
  .card { background: var(--card); border: 1.5px solid var(--border); border-radius: var(--radius2); padding: 20px; box-shadow: var(--shadow); }
  @media (max-width: 600px) { .card { padding: 14px; } }
  .card-title { font-family: var(--display); font-weight: 700; font-size: 15px; margin-bottom: 4px; color: var(--text); }
  .card-sub { font-size: 12px; color: var(--text2); }

  /* ── KPI grid ── */
  .kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 18px; }
  @media (max-width: 900px) { .kpi-grid { grid-template-columns: repeat(2, 1fr); } }
  @media (max-width: 480px) { .kpi-grid { grid-template-columns: 1fr 1fr; gap: 10px; } }
  .kpi { background: var(--card); border: 1.5px solid var(--border); border-radius: var(--radius2); padding: 18px 16px; position: relative; box-shadow: var(--shadow); overflow: hidden; }
  .kpi::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px; border-radius: 12px 12px 0 0; }
  .kpi.blue::before { background: var(--primary); }
  .kpi.teal::before { background: var(--accent); }
  .kpi.green::before { background: var(--success); }
  .kpi.amber::before { background: var(--warning); }
  .kpi-icon { width: 36px; height: 36px; border-radius: 9px; display: flex; align-items: center; justify-content: center; font-size: 17px; margin-bottom: 10px; }
  .kpi.blue .kpi-icon  { background: var(--primaryL); }
  .kpi.teal .kpi-icon  { background: var(--accentL); }
  .kpi.green .kpi-icon { background: var(--successL); }
  .kpi.amber .kpi-icon { background: var(--warningL); }
  .kpi-label { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: var(--text2); margin-bottom: 6px; }
  .kpi-value { font-family: var(--display); font-weight: 800; font-size: 26px; letter-spacing: -0.5px; color: var(--text); }
  .kpi-change { font-size: 11px; margin-top: 4px; font-weight: 600; }
  .kpi-change.up { color: var(--success); }
  .kpi-change.dn { color: var(--danger); }

  /* ── Grid helpers ── */
  .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
  .grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; }
  @media (max-width: 900px) { .grid-3 { grid-template-columns: repeat(2, 1fr); } }
  @media (max-width: 600px) { .grid-2, .grid-3 { grid-template-columns: 1fr; gap: 10px; } }
  .flex { display: flex; }
  .flex-col { display: flex; flex-direction: column; }
  .items-center { align-items: center; }
  .justify-between { justify-content: space-between; }
  .gap-8  { gap: 8px; }
  .gap-12 { gap: 12px; }
  .gap-16 { gap: 16px; }
  .mb-4  { margin-bottom: 4px; }
  .mb-8  { margin-bottom: 8px; }
  .mb-14 { margin-bottom: 14px; }
  .mb-20 { margin-bottom: 20px; }
  .mt-8  { margin-top: 8px; }
  .mt-12 { margin-top: 12px; }
  .flex-1 { flex: 1; }
  .min-w-0 { min-width: 0; }
  .w-full  { width: 100%; }
  .text-sm { font-size: 12px; }
  .text-xs { font-size: 11px; }
  .text-muted   { color: var(--text2); }
  .text-primary { color: var(--primary); }
  .text-success { color: var(--success); }
  .text-danger  { color: var(--danger); }
  .text-warning { color: var(--warning); }
  .fw-500 { font-weight: 500; }
  .fw-600 { font-weight: 600; }
  .fw-700 { font-weight: 700; }

  /* ── Buttons ── */
  .btn { display: inline-flex; align-items: center; gap: 6px; padding: 8px 14px; border-radius: 9px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.15s; border: 1.5px solid transparent; white-space: nowrap; }
  .btn-primary { background: var(--primary); color: #fff; box-shadow: 0 2px 8px rgba(255,91,46,0.22); }
  .btn-primary:hover { background: var(--primary2); }
  .btn-outline { background: transparent; border-color: var(--border2); color: var(--text2); }
  .btn-outline:hover { border-color: var(--primary); color: var(--primary); background: var(--primaryL); }
  .btn-ghost { background: transparent; color: var(--text2); }
  .btn-ghost:hover { background: var(--bg); color: var(--text); }
  .btn-danger { background: var(--dangerL); color: var(--danger); border-color: #FECACA; }
  .btn-danger:hover { background: #FEE2E2; }
  .btn-sm { padding: 5px 10px; font-size: 12px; border-radius: 7px; }
  .btn-icon { width: 32px; height: 32px; padding: 0; border-radius: 8px; justify-content: center; }

  /* ── Tags ── */
  .tag { display: inline-flex; align-items: center; gap: 4px; padding: 3px 8px; border-radius: 6px; font-size: 11px; font-weight: 600; }
  .tag-blue   { background: var(--primaryL);  color: var(--primary); }
  .tag-teal   { background: var(--accentL);   color: var(--accent); }
  .tag-green  { background: var(--successL);  color: var(--success); }
  .tag-amber  { background: var(--warningL);  color: var(--warning); }
  .tag-red    { background: var(--dangerL);   color: var(--danger); }
  .tag-purple { background: var(--purpleL);   color: var(--purple); }

  /* ── Table ── */
  .table { width: 100%; border-collapse: collapse; }
  .table th { text-align: left; padding: 10px 14px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: var(--muted); border-bottom: 1.5px solid var(--border); background: var(--bg); }
  .table td { padding: 12px 14px; border-bottom: 1px solid var(--border); font-size: 13px; }
  .table tr:last-child td { border-bottom: none; }
  .table tr:hover td { background: rgba(250,247,240,0.85); }
  .table-wrap { overflow-x: auto; }

  /* ── Progress ── */
  .prog-bar { height: 5px; background: var(--border); border-radius: 99px; overflow: hidden; }
  .prog-fill { height: 100%; border-radius: 99px; transition: width 0.8s ease; }

  /* ── Inputs ── */
  .input-wrap { display: flex; align-items: center; gap: 8px; background: var(--bg); border: 1.5px solid var(--border); border-radius: 9px; padding: 0 12px; height: 38px; transition: border-color 0.15s; }
  .input-wrap:focus-within { border-color: var(--primary); }
  .input-wrap input { flex: 1; font-size: 13px; background: transparent; }
  .input-wrap input::placeholder { color: var(--muted); }
  .input-full { width: 100%; background: var(--bg); border: 1.5px solid var(--border); border-radius: 9px; padding: 10px 12px; font-size: 13.5px; color: var(--text); transition: border-color 0.15s; }
  .input-full:focus { border-color: var(--primary); outline: none; background: #fff; }

  /* ── Dot ── */
  .dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
  .dot-green { background: var(--success); }
  .dot-amber { background: var(--warning); }
  .dot-red   { background: var(--danger); }
  .dot-blue  { background: var(--primary); }

  /* ── Divider ── */
  .divider { height: 1px; background: var(--border); margin: 16px 0; }

  /* ── Toggle ── */
  .toggle { width: 40px; height: 22px; border-radius: 11px; background: var(--border2); position: relative; cursor: pointer; transition: background 0.2s; flex-shrink: 0; border: 1.5px solid transparent; }
  .toggle.on { background: var(--primary); }
  .toggle::after { content: ''; position: absolute; width: 16px; height: 16px; border-radius: 50%; background: #fff; top: 1px; left: 1px; transition: transform 0.2s; box-shadow: 0 1px 3px rgba(0,0,0,0.2); }
  .toggle.on::after { transform: translateX(18px); }

  /* ── Sparkline ── */
  .sparkline { height: 50px; display: flex; align-items: flex-end; gap: 3px; }
  .spark-bar { flex: 1; background: linear-gradient(180deg, var(--primary), var(--accent-2)); border-radius: 3px 3px 0 0; opacity: 0.75; min-height: 4px; transition: opacity 0.15s; }
  .spark-bar:hover { opacity: 1; }

  /* ── Chat (Claude Desktop–style) ── */
  .chat-shell { display: flex; flex-direction: column; height: calc(100vh - var(--top-h)); background: var(--bg); }
  .chat-shell-claude .chat-toolbar {
    padding: 8px 20px; border-bottom: 1px solid var(--border);
    background: var(--bg);
    backdrop-filter: none;
    gap: 8px;
  }
  .chat-toolbar {
    padding: 10px 16px; border-bottom: 1px solid var(--border);
    background: var(--surface);
    display: flex; align-items: center; gap: 10px; flex-wrap: wrap; flex-shrink: 0;
  }
  .chat-toolbar-label { text-transform: uppercase; letter-spacing: 0.5px; }
  .chat-kb-select {
    background: var(--bg); border: 1.5px solid var(--border); border-radius: 8px;
    padding: 5px 10px; font-size: 13px; color: var(--text); min-width: 180px;
  }
  .chat-clear-btn { margin-left: auto; }
  .chat-msgs {
    flex: 1; overflow-y: auto; padding: 24px 20px 16px;
    display: flex; flex-direction: column; align-items: center;
    background: var(--bg);
  }
  .chat-msgs-inner { width: 100%; max-width: var(--chat-col); display: flex; flex-direction: column; gap: 20px; }
  .chat-shell-claude .msg { max-width: 100%; gap: 16px; }
  .chat-shell-claude .msg.user { margin-left: 0; align-self: flex-end; max-width: 85%; }
  .chat-shell-claude .msg.assistant-wide { max-width: 100%; width: 100%; }
  .msg { display: flex; gap: 10px; max-width: 800px; width: 100%; }
  .msg.user { flex-direction: row-reverse; margin-left: auto; }
  .msg-avatar { width: 28px; height: 28px; border-radius: 8px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 700; font-family: var(--sans); }
  .msg-avatar.ai { background: var(--primary); color: #fff; }
  .msg-avatar.user { background: var(--sidebar-active-bg); color: var(--text); border: 1px solid var(--border); }
  .msg-bubble { background: transparent; border: none; border-radius: 0; padding: 0; font-size: 15px; line-height: 1.65; color: var(--text); box-shadow: none; font-family: var(--sans); }
  .chat-shell-claude .msg.user .msg-bubble {
    background: #ececea; border-radius: 18px; padding: 12px 16px;
    font-family: var(--sans); font-size: 14px;
  }
  .msg.user .msg-bubble { background: var(--primaryL); border-radius: 12px; padding: 12px 15px; border: 1px solid rgba(198,97,63,0.15); }
  .msg-meta { font-size: 11px; color: var(--muted); margin-top: 6px; font-family: var(--sans); }
  .chat-io-label {
    font-family: var(--display); font-size: 10px; font-weight: 800; letter-spacing: 0.12em;
    color: var(--muted); margin-bottom: 6px;
  }
  .chat-io-input { text-align: right; }
  .chat-output-block { margin-bottom: 12px; }
  .chat-live-answer { white-space: pre-wrap; font-size: 14px; line-height: 1.65; color: var(--text); }
  .chat-log-bar {
    display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-bottom: 0;
    padding: 10px 12px; border-radius: 10px; border: 1.5px solid rgba(14,17,22,0.08);
    background: rgba(255,253,246,0.85);
  }
  .chat-l2-workflow {
    margin: 0 16px 12px; display: flex; flex-direction: column; gap: 10px;
    flex-shrink: 0;
  }
  .chat-l2-block {
    border: 1.5px solid rgba(14,17,22,0.08); border-radius: 12px;
    background: var(--card, #fffdf6); box-shadow: var(--shadow, 0 1px 3px rgba(14,17,22,0.06));
    overflow: hidden;
  }
  .chat-l2-block-head {
    display: flex; align-items: flex-start; gap: 10px; width: 100%; text-align: left;
    padding: 12px 14px; border: none; background: transparent; cursor: pointer;
    font: inherit; color: inherit;
  }
  .chat-l2-block-head-static { cursor: default; padding-bottom: 8px; }
  .chat-l2-block-head:hover:not(.chat-l2-block-head-static) { background: rgba(14,17,22,0.03); }
  .chat-l2-num {
    flex-shrink: 0; width: 26px; height: 26px; border-radius: 8px;
    display: flex; align-items: center; justify-content: center;
    font-size: 13px; font-weight: 800; background: var(--primaryL, #fff0eb); color: var(--primary);
    border: 1px solid rgba(255,91,46,0.25);
  }
  .chat-l2-head-text { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
  .chat-l2-title { font-family: var(--display); font-size: 14px; font-weight: 700; color: var(--text); }
  .chat-l2-sub { font-size: 12px; color: var(--muted); line-height: 1.45; }
  .chat-l2-chevron { color: var(--muted); font-size: 12px; margin-top: 4px; }
  .chat-l2-checklist {
    list-style: none; margin: 0; padding: 0 14px 14px 14px;
    display: flex; flex-direction: column; gap: 10px; border-top: 1px solid rgba(14,17,22,0.06);
    padding-top: 12px;
  }
  .chat-l2-checklist li { display: flex; gap: 8px; align-items: flex-start; }
  .chat-l2-check-num { font-size: 12px; font-weight: 800; color: var(--muted); min-width: 18px; }
  .chat-l2-log-block .chat-l2-block-head-static { border-bottom: none; }
  .chat-l2-log-intro { margin: 0 14px 8px; line-height: 1.5; }
  .chat-l2-log-hint { margin: 8px 14px 0; line-height: 1.5; }
  .chat-l2-signin-hint { margin: 0 14px 14px; }
  .chat-log-examples { padding: 0 14px 14px; display: flex; flex-direction: column; gap: 8px; }
  .chat-log-examples-label { text-transform: uppercase; letter-spacing: 0.05em; }
  .chat-log-example-chips { padding: 0 !important; }
  .chat-log-bar-label { font-size: 11px; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase; color: var(--muted); }
  .chat-log-select { min-width: 160px; }
  .chat-log-search {
    flex: 1; min-width: 200px; font-size: 12px; padding: 6px 10px; border-radius: 8px;
    border: 1.5px solid var(--border); background: var(--bg);
  }
  .chat-troubleshoot-list { margin: 0; padding-left: 18px; display: flex; flex-direction: column; gap: 12px; }
  .chat-log-lines { max-height: 220px; overflow: auto; font-size: 11px; }
  .chat-input-area {
    padding: 12px 20px 20px; border-top: none;
    background: var(--bg);
    flex-shrink: 0;
    display: flex; flex-direction: column; align-items: center;
  }
  .chat-shell-claude .chat-input-area { padding-bottom: max(20px, env(safe-area-inset-bottom)); }
  .chat-input-box {
    display: flex; align-items: flex-end; gap: 8px; width: 100%; max-width: var(--chat-col);
    background: var(--card); border: 1px solid var(--border2);
    border-radius: 20px; padding: 12px 14px 12px 18px;
    transition: border-color 0.15s, box-shadow 0.15s;
    box-shadow: 0 2px 12px rgba(0,0,0,0.06);
  }
  .chat-input-box:focus-within { border-color: rgba(198,97,63,0.45); box-shadow: 0 4px 20px rgba(0,0,0,0.08); }
  .chat-textarea { flex: 1; resize: none; font-size: 15px; line-height: 1.5; min-height: 24px; max-height: 160px; color: var(--text); background: transparent; font-family: var(--sans); }
  .chat-textarea::placeholder { color: var(--muted); }
  .send-btn { width: 36px; height: 36px; border-radius: 12px; flex-shrink: 0; background: var(--primary); color: #fff; display: flex; align-items: center; justify-content: center; font-size: 16px; transition: all 0.15s; box-shadow: none; }
  .send-btn:hover { background: var(--primary2); }
  .send-btn:disabled { background: var(--border2); color: var(--muted); cursor: not-allowed; }
  .chat-knowledge-banner {
    display: none;
  }
  .chat-shell-claude .chat-l2-workflow {
    margin: 0; padding: 0 20px 12px; max-width: calc(var(--chat-col) + 40px); width: 100%; align-self: center;
  }
  .chat-shell-claude .chat-l2-block { border-radius: 14px; border-color: var(--border); box-shadow: none; background: var(--card); }
  .chat-shell-claude .output-section { border-radius: 14px; border: 1px solid var(--border); background: var(--card); }
  .chat-welcome-hero {
    text-align: center; padding: 48px 12px 24px; margin: 0;
    background: transparent; border: none; box-shadow: none;
  }
  .chat-welcome-mark {
    width: 48px; height: 48px; border-radius: 14px; margin: 0 auto 16px;
    background: var(--primary);
    display: grid; place-items: center;
    font-family: var(--display); font-style: italic; font-size: 24px; color: #fff;
    box-shadow: none; animation: none;
  }
  .chat-welcome-mark::after { display: none; }
  .chat-welcome-title { font-family: var(--display); font-weight: 400; font-size: 28px; margin-bottom: 8px; letter-spacing: -0.02em; }
  .chat-welcome-title em { font-style: italic; color: var(--primary); }
  .chat-welcome-sub { font-family: var(--sans); font-size: 14px; color: var(--text2); max-width: 420px; margin: 0 auto; line-height: 1.6; }
  .chat-suggestions { justify-content: center; max-width: var(--chat-col); margin: 0 auto; }
  .chat-chip {
    border-radius: 999px; border: 1px solid var(--border2); background: var(--card);
    font-family: var(--sans); font-size: 13px;
  }
  .chat-chip:hover { border-color: var(--primary); background: var(--primaryL); }
  .chat-onboarding-hint {
    margin: 0 20px 10px; max-width: var(--chat-col); width: 100%; align-self: center;
    padding: 10px 14px; border-radius: 12px; font-size: 13px; line-height: 1.5; color: var(--text2);
    background: var(--primaryL); border: 1px solid rgba(198,97,63,0.2); font-family: var(--sans);
  }
  .chat-onboarding-hint span:first-child { font-weight: 700; color: var(--primary); margin-right: 6px; }
  .chat-how-steps {
    display: flex; flex-direction: column; gap: 8px; margin-top: 20px; text-align: left; max-width: 400px; margin-left: auto; margin-right: auto;
  }
  .chat-how-step {
    display: flex; align-items: flex-start; gap: 10px; font-size: 13px; color: var(--text2); font-family: var(--sans); line-height: 1.45;
  }
  .chat-how-num {
    flex-shrink: 0; width: 22px; height: 22px; border-radius: 6px; background: var(--card); border: 1px solid var(--border);
    display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700; color: var(--text);
  }
  .chat-starters-label { font-size: 12px; font-weight: 700; color: var(--muted); text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 8px; font-family: var(--sans); }
  .chat-more-starters { align-self: center; margin: 4px 0 12px; }
  .chat-chip-muted { opacity: 0.92; font-size: 12px !important; }
  .chat-input-hint { width: 100%; max-width: var(--chat-col); font-family: var(--sans); }
  .main-chat .topbar { display: none; }
  .main-chat .chat-shell { height: 100vh; }
  .sidebar-new-chat {
    margin: 8px 12px 4px; padding: 10px 14px; border-radius: 10px; width: calc(100% - 24px);
    background: var(--card); border: 1px solid var(--border2); font-size: 13px; font-weight: 600;
    font-family: var(--sans); color: var(--text); display: flex; align-items: center; gap: 8px;
    transition: background 0.15s, border-color 0.15s;
  }
  .sidebar-new-chat:hover { border-color: var(--primary); background: var(--primaryL); color: var(--primary); }
  .dash-how-row {
    display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 16px;
  }
  @media (max-width: 768px) { .dash-how-row { grid-template-columns: 1fr; } }
  .dash-how-card {
    padding: 14px 16px; border-radius: 14px; border: 1px solid var(--border); background: var(--card);
    font-family: var(--sans); border-left: 3px solid var(--primary);
    background: linear-gradient(90deg, var(--primaryL), var(--card) 48%);
  }
  .dash-how-card:nth-child(2) { border-left-color: var(--accent); background: linear-gradient(90deg, var(--accentL), var(--card) 48%); }
  .dash-how-card:nth-child(3) { border-left-color: #3b82f6; background: linear-gradient(90deg, #eff6ff, var(--card) 48%); }
  .dash-how-card strong { display: block; font-size: 14px; margin-bottom: 4px; color: var(--text); }
  .dash-how-card span { font-size: 13px; color: var(--text2); line-height: 1.45; }
  .typing-dot { display: inline-block; width: 6px; height: 6px; border-radius: 50%; background: var(--primary); animation: tdot 1.2s infinite; margin: 0 2px; }
  .typing-dot:nth-child(2) { animation-delay: 0.2s; }
  .typing-dot:nth-child(3) { animation-delay: 0.4s; }
  @keyframes tdot { 0%,80%,100%{transform:scale(0.6);opacity:0.3} 40%{transform:scale(1);opacity:1} }

  /* ── KB cards ── */
  .kb-card { background: var(--card); border: 1.5px solid var(--border); border-radius: var(--radius2); padding: 18px; cursor: pointer; transition: all 0.2s; display: flex; flex-direction: column; gap: 12px; box-shadow: var(--shadow); }
  .kb-card:hover { border-color: var(--primary); transform: translateY(-2px); box-shadow: var(--shadow2); }
  .kb-icon { width: 44px; height: 44px; border-radius: 11px; display: flex; align-items: center; justify-content: center; font-size: 22px; }

  /* ── Agent cards ── */
  .agent-card { background: var(--card); border: 1.5px solid var(--border); border-radius: var(--radius2); padding: 18px; transition: all 0.2s; box-shadow: var(--shadow); }
  .agent-card:hover { box-shadow: var(--shadow2); }

  /* ── Integration catalog ── */
  .int-card { background: var(--card); border: 1.5px solid var(--border); border-radius: var(--radius2); padding: 16px; display: flex; align-items: center; gap: 14px; box-shadow: var(--shadow); transition: all 0.15s; }
  .int-card:hover { border-color: var(--border2); }
  .int-card-rich { cursor: pointer; align-items: flex-start; }
  .int-card-rich:hover { border-color: var(--primary); box-shadow: var(--shadow2); transform: translateY(-1px); }
  .int-card-rich.selected { border-color: var(--primary); background: var(--card); box-shadow: 0 0 0 1px var(--primary); }
  .int-logo { width: 44px; height: 44px; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 22px; background: var(--bg); border: 1.5px solid var(--border); flex-shrink: 0; }
  .int-card-actions { flex-shrink: 0; display: flex; flex-direction: column; gap: 6px; }
  .int-toolbar { display: flex; flex-wrap: wrap; gap: 12px; align-items: center; margin-bottom: 12px; }
  .int-search { flex: 1; min-width: 200px; max-width: 420px; }
  .int-status-filters, .int-cat-row { display: flex; flex-wrap: wrap; gap: 8px; }
  .int-cat-row { margin-bottom: 18px; }
  .int-filter-chip, .int-cat-chip {
    padding: 6px 12px; border-radius: 8px; font-size: 12px; font-weight: 600;
    border: 1.5px solid var(--border); background: var(--surface); color: var(--text2);
    transition: all 0.15s;
  }
  .int-filter-chip:hover, .int-cat-chip:hover { border-color: var(--primary); color: var(--primary); background: var(--primaryL); }
  .int-filter-chip.active, .int-cat-chip.active { border-color: var(--primary); background: var(--primaryL); color: var(--primary); }
  .int-group { margin-bottom: 22px; }
  .int-group-title {
    font-family: var(--display); font-weight: 700; font-size: 13px;
    text-transform: uppercase; letter-spacing: 0.5px; color: var(--muted);
    margin-bottom: 10px;
  }
  .int-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  @media (max-width: 900px) { .int-grid { grid-template-columns: 1fr; } }
  .int-env-chip {
    display: inline-block; font-size: 11px; padding: 4px 8px; border-radius: 6px;
    background: var(--bg); border: 1px solid var(--border); color: var(--text2);
  }
  .int-steps-list { margin: 0 0 8px 18px; padding: 0; }
  .int-steps-list li { font-size: 13px; color: var(--text2); line-height: 1.55; margin-bottom: 8px; }
  .int-drawer-backdrop {
    position: fixed; inset: 0; background: rgba(17, 24, 39, 0.35); z-index: 400;
    animation: fadeIn 0.2s ease;
  }
  .int-drawer {
    position: fixed; top: 0; right: 0; bottom: 0; width: min(440px, 100vw);
    background: var(--surface); z-index: 410; display: flex; flex-direction: column;
    box-shadow: -8px 0 32px rgba(0,0,0,0.12);
    animation: intDrawerIn 0.28s cubic-bezier(.4,0,.2,1);
  }
  @keyframes intDrawerIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
  .int-drawer-head {
    padding: 18px 20px; border-bottom: 1px solid var(--border);
    display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; flex-shrink: 0;
  }
  .int-drawer-body { flex: 1; overflow-y: auto; padding: 18px 20px; }
  .int-detail-block { margin-bottom: 20px; padding-bottom: 18px; border-bottom: 1px solid var(--border); }
  .int-detail-block:last-child { margin-bottom: 0; padding-bottom: 0; border-bottom: none; }
  .int-drawer-foot {
    padding: 14px 20px; border-top: 1px solid var(--border);
    display: flex; align-items: center; gap: 10px; flex-wrap: wrap; flex-shrink: 0;
    background: var(--card);
  }
  .int-modal-backdrop {
    position: fixed; inset: 0; background: rgba(17, 24, 39, 0.45); z-index: 500;
    display: flex; align-items: center; justify-content: center; padding: 16px;
    animation: fadeIn 0.2s ease;
  }
  .int-modal {
    width: min(480px, 100%); background: var(--surface); border-radius: var(--radius2);
    border: 1.5px solid var(--border); box-shadow: var(--shadow2); overflow: hidden;
  }
  .int-modal-head {
    padding: 16px 18px; border-bottom: 1px solid var(--border);
    display: flex; align-items: center; justify-content: space-between; gap: 12px;
  }
  .int-modal-body { padding: 18px; }
  .int-modal-foot {
    padding: 14px 18px; border-top: 1px solid var(--border);
    display: flex; align-items: center; justify-content: space-between; gap: 10px; background: var(--card);
  }
  .int-setup-progress { display: flex; gap: 6px; margin-bottom: 14px; }
  .int-setup-dot { flex: 1; height: 4px; border-radius: 99px; background: var(--border); }
  .int-setup-dot.active { background: var(--primary); }
  .int-setup-step {
    display: flex; gap: 12px; align-items: flex-start;
    background: var(--bg); border: 1.5px solid var(--border); border-radius: 11px; padding: 14px;
  }
  .int-setup-num {
    width: 28px; height: 28px; border-radius: 8px; flex-shrink: 0;
    background: var(--primaryL); color: var(--primary); font-weight: 800;
    display: flex; align-items: center; justify-content: center; font-size: 13px;
    font-family: var(--display);
  }
  .int-env-box {
    background: linear-gradient(180deg, var(--card) 0%, var(--bg) 100%);
    border: 1.5px solid rgba(255,91,46,0.28); border-radius: 11px; padding: 12px 14px;
  }
  .flex-wrap { flex-wrap: wrap; }

  /* ── Notif item ── */
  .notif-item { display: flex; gap: 12px; padding: 13px 0; border-bottom: 1px solid var(--border); }
  .notif-item:last-child { border-bottom: none; }

  /* ── Audit row ── */
  .log-row { display: flex; gap: 12px; padding: 12px 0; border-bottom: 1px solid var(--border); align-items: flex-start; }
  .log-row:last-child { border-bottom: none; }

  /* ── Settings ── */
  .settings-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.6px; color: var(--muted); margin-bottom: 10px; }
  .settings-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 14px 0; border-bottom: 1px solid var(--border); }
  .settings-row:last-child { border-bottom: none; }

  /* ── Plan cards ── */
  .plan-card { background: var(--card); border: 1.5px solid var(--border); border-radius: var(--radius2); padding: 22px; box-shadow: var(--shadow); }
  .plan-card.active-plan { border-color: var(--primary); background: var(--card); }
  .plan-price { font-family: var(--display); font-weight: 800; font-size: 30px; letter-spacing: -0.5px; color: var(--text); }
  .plan-feature { display: flex; align-items: center; gap: 8px; font-size: 13px; padding: 5px 0; color: var(--text2); }

  /* ── Toast ── */
  .toast-wrap { position: fixed; bottom: 20px; right: 20px; z-index: 9999; display: flex; flex-direction: column; gap: 8px; }
  @media (max-width: 480px) { .toast-wrap { bottom: 12px; right: 12px; left: 12px; } }
  .toast { background: var(--surface); border: 1.5px solid var(--border); border-radius: 10px; padding: 11px 16px; font-size: 13px; display: flex; align-items: center; gap: 10px; box-shadow: 0 8px 28px rgba(0,0,0,0.12); animation: toastIn 0.3s ease; }
  @keyframes toastIn { from{transform:translateY(12px);opacity:0} to{transform:translateY(0);opacity:1} }

  /* ── Ops structured chat ── */
  .chat-assistant-content { display: flex; flex-direction: column; gap: 12px; }
  .chat-prose-wrap { padding: 0 2px; max-width: 42rem; }
  .chat-prose-doc { font-family: var(--sans); font-size: 15px; line-height: 1.6; color: var(--text); }
  .chat-prose { font-size: 15px; line-height: 1.6; color: var(--text); }
  .chat-prose-p { margin: 0 0 12px; color: var(--text2); }
  .chat-prose-p:last-child { margin-bottom: 0; }
  .chat-prose-h { font-family: var(--display); font-weight: 700; color: var(--text); margin: 22px 0 10px; line-height: 1.3; }
  .chat-prose-h:first-child { margin-top: 0; }
  .chat-prose-h2 { font-size: 22px; }
  .chat-prose-exec-summary {
    font-size: 24px; letter-spacing: -0.02em;
    padding-bottom: 8px; margin-bottom: 14px;
    border-bottom: 2px solid var(--primary, #c6613f);
  }
  .chat-prose-section-meta { color: var(--text2); }
  .chat-prose-pre-mermaid .chat-prose-pre {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 13px; line-height: 1.45;
    background: linear-gradient(180deg, var(--bg) 0%, var(--card) 100%);
  }
  .chat-prose-h3 { font-size: 18px; }
  .chat-prose-h4 { font-size: 16px; }
  .chat-prose-h5 { font-size: 14px; color: var(--text2); }
  .chat-prose-ul, .chat-prose-ol { margin: 0 0 14px; padding-left: 1.35rem; color: var(--text2); }
  .chat-prose-ul li, .chat-prose-ol li { margin-bottom: 8px; }
  .chat-prose-ul li:last-child, .chat-prose-ol li:last-child { margin-bottom: 0; }
  .chat-prose-code {
    font-family: var(--mono-f); font-size: 12px; padding: 2px 7px; border-radius: 6px;
    background: rgba(198, 97, 63, 0.1); border: 1px solid rgba(198, 97, 63, 0.2); color: var(--primary2);
  }
  .chat-prose-table-wrap { margin: 12px 0 18px; overflow-x: auto; border-radius: 12px; border: 1px solid var(--border); background: var(--card); }
  .chat-prose-table { width: 100%; border-collapse: collapse; font-size: 14px; }
  .chat-prose-table th { text-align: left; padding: 10px 14px; font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: var(--muted); border-bottom: 1px solid var(--border); background: var(--bg); }
  .chat-prose-table td { padding: 10px 14px; border-bottom: 1px solid var(--border); vertical-align: top; }
  .chat-prose-table tr:last-child td { border-bottom: none; }
  .chat-prose-td-param { font-weight: 600; color: var(--text); }
  .chat-prose-td-val {
    display: inline-block; padding: 2px 10px; border-radius: 8px;
    background: rgba(245, 158, 11, 0.15); border: 1px solid rgba(245, 158, 11, 0.35);
    font-family: var(--mono-f); font-size: 13px; color: var(--text);
  }
  .chat-prose-pre-wrap {
    margin: 0 0 16px; border-radius: 12px; overflow: hidden;
    border: 1px solid var(--border); background: #f0f0eb;
  }
  .chat-prose-pre-toolbar {
    display: flex; align-items: center; justify-content: space-between;
    padding: 8px 12px; background: #e8e8e2; border-bottom: 1px solid var(--border);
  }
  .chat-prose-copy {
    font-size: 12px; font-weight: 600; color: var(--text2); padding: 4px 10px; border-radius: 8px;
    border: 1px solid var(--border2); background: var(--card);
  }
  .chat-prose-copy:hover { border-color: var(--primary); color: var(--primary); }
  .chat-prose-pre {
    margin: 0; padding: 14px 16px; overflow-x: auto;
    background: transparent; color: var(--text); font-family: var(--mono-f); font-size: 13px; line-height: 1.55;
    border: none;
  }
  .chat-prose-pre-lang {
    font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em;
    color: var(--muted);
  }
  .chat-prose-pre-streaming { box-shadow: inset 0 0 0 1px rgba(198, 97, 63, 0.35); }
  .chat-prose-stream-cursor {
    display: inline-block; width: 2px; height: 1em; margin-left: 2px; vertical-align: text-bottom;
    background: var(--teal, #14b8a6); animation: chat-prose-blink 1s step-end infinite;
  }
  @keyframes chat-prose-blink { 50% { opacity: 0; } }
  .chat-stream-wait { display: flex; align-items: center; gap: 6px; padding: 4px 0; min-height: 28px; }
  .ent-answer-body .chat-prose { font-size: 14px; }
  /* ── Infrastructure intelligence (embedded chat) ── */
  .infra-block { display: flex; flex-direction: column; gap: 12px; }
  .infra-sev-pill {
    display: inline-flex; align-items: center; gap: 8px; flex-wrap: wrap;
    padding: 6px 12px; border-radius: 99px; border: 1.5px solid var(--border);
    background: var(--surface); max-width: 100%;
  }
  .infra-sev-critical, .infra-sev-error { border-color: rgba(239,68,68,0.35); background: var(--dangerL, #FEF2F2); }
  .infra-sev-warning { border-color: rgba(245,158,11,0.35); background: var(--warningL); }
  .infra-sev-info { border-color: rgba(34,197,94,0.35); background: var(--successL); }
  .infra-sev-label { font-size: 10px; font-weight: 800; letter-spacing: 0.6px; color: var(--text); }
  .infra-sev-dash { color: var(--muted); font-size: 10px; }
  .infra-sev-summary { font-size: 12px; color: var(--text2); line-height: 1.45; }
  .infra-chart-wrap {
    background: var(--surface); border: 1.5px solid var(--border); border-radius: 11px; overflow: hidden;
  }
  .infra-chart-head {
    display: flex; align-items: center; justify-content: space-between; gap: 10px;
    padding: 10px 14px; border-bottom: 1px solid var(--border); background: var(--bg);
  }
  .infra-chart-title { font-size: 11px; color: var(--text2); font-family: var(--mono-f); }
  .infra-chart-body { height: 180px; padding: 8px 4px 4px; }
  .infra-chart-legend {
    display: flex; gap: 12px; padding: 6px 14px 10px; border-top: 1px solid var(--border);
  }
  .infra-chart-legend-item {
    display: flex; align-items: center; gap: 5px; font-size: 9px; color: var(--muted); font-family: var(--mono-f);
  }
  .infra-chart-legend-swatch { width: 12px; height: 2px; border-radius: 1px; display: inline-block; }
  .infra-chart-tip {
    background: var(--ink); border: 1px solid var(--border); border-radius: 8px;
    padding: 8px 12px; font-size: 11px; font-family: var(--mono-f);
  }
  .infra-chart-tip-label { color: var(--muted); margin-bottom: 4px; }
  .infra-chart-tip-row { display: flex; gap: 8px; }
  .infra-log-wrap {
    background: var(--surface); border: 1.5px solid var(--border); border-radius: 11px; overflow: hidden;
    font-family: var(--mono-f);
  }
  .infra-log-head {
    display: flex; flex-wrap: wrap; align-items: center; gap: 8px;
    padding: 8px 12px; border-bottom: 1px solid var(--border); background: var(--bg);
  }
  .infra-log-title { font-size: 10px; color: var(--muted); flex: 1; min-width: 140px; }
  .infra-log-filters { display: flex; gap: 4px; flex-wrap: wrap; }
  .infra-log-filter {
    padding: 2px 8px; border-radius: 4px; border: 1px solid var(--border);
    background: transparent; color: var(--muted); font-size: 9px; cursor: pointer; font-family: var(--mono-f);
  }
  .infra-log-filter.active { background: var(--primaryL); color: var(--primary); border-color: rgba(255,91,46,0.28); }
  .infra-log-search { padding: 6px 12px; border-bottom: 1px solid var(--border); }
  .infra-log-search-input {
    width: 100%; background: transparent; border: none; color: var(--text2);
    font-size: 10px; outline: none; font-family: var(--mono-f);
  }
  .infra-log-scroll { max-height: 200px; overflow-y: auto; }
  .infra-log-row {
    display: grid; gap: 8px; padding: 5px 12px; border-bottom: 1px solid var(--border);
    grid-template-columns: 90px 50px 110px 1fr; align-items: start;
  }
  .infra-log-row:last-child { border-bottom: none; }
  .infra-log-bg-fatal { background: rgba(239,68,68,0.06); }
  .infra-log-bg-error { background: rgba(239,68,68,0.04); }
  .infra-log-bg-warn { background: rgba(245,158,11,0.05); }
  .infra-log-ts { color: var(--muted); font-size: 9px; }
  .infra-log-lv { font-size: 9px; font-weight: 700; }
  .infra-log-fatal, .infra-log-error { color: var(--danger); }
  .infra-log-warn { color: var(--warning); }
  .infra-log-info { color: var(--muted); }
  .infra-log-svc { color: var(--primary); font-size: 9px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .infra-log-msg { font-size: 9px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .infra-rca-panel, .infra-fix-panel {
    background: var(--surface); border: 1.5px solid var(--border); border-radius: 11px; overflow: hidden;
  }
  .infra-fix-panel { border-color: rgba(34,197,94,0.25); background: linear-gradient(180deg, var(--successL) 0%, var(--surface) 100%); }
  .infra-panel-head {
    display: flex; align-items: center; gap: 6px; padding: 8px 14px;
    border-bottom: 1px solid var(--border); background: var(--bg);
  }
  .infra-panel-head-fix { border-bottom-color: rgba(34,197,94,0.2); background: var(--successL); }
  .infra-panel-icon { color: var(--primary); font-size: 10px; }
  .infra-panel-head-fix .infra-panel-icon { color: var(--success); }
  .infra-panel-label {
    font-size: 10px; color: var(--muted); font-family: var(--mono-f); letter-spacing: 0.5px;
    text-transform: uppercase; font-weight: 700;
  }
  .infra-panel-head-fix .infra-panel-label { color: var(--success); }
  .infra-panel-body { padding: 12px 14px; font-size: 13px; color: var(--text2); line-height: 1.7; }
  .infra-fix-pre {
    padding: 10px 14px; font-size: 11px; color: var(--success); font-family: var(--mono-f);
    background: transparent; margin: 0; white-space: pre-wrap; line-height: 1.7;
  }
  .infra-source-chips { display: flex; gap: 6px; flex-wrap: wrap; }
  .infra-source-chip {
    font-size: 9px; padding: 3px 8px; border-radius: 5px; border: 1px solid var(--border);
    color: var(--muted); background: var(--bg); font-family: var(--mono-f);
  }
  .infra-welcome-pills {
    display: flex; gap: 6px; flex-wrap: wrap; justify-content: center;
    padding: 0 16px 12px;
  }
  .infra-welcome-pill {
    font-size: 9px; padding: 3px 8px; border-radius: 5px; border: 1px solid var(--border);
    color: var(--muted); background: var(--surface);
  }
  .infra-typing-label {
    display: block; margin-top: 8px; font-size: 11px; color: var(--muted); font-family: var(--mono-f);
  }
  /* ── Platform architecture page ── */
  .arch-page { display: flex; flex-direction: column; gap: 16px; }
  .arch-tabs { display: flex; gap: 6px; flex-wrap: wrap; border-bottom: 1px solid var(--border); padding-bottom: 0; }
  .arch-tab {
    padding: 10px 14px; border: none; background: transparent; cursor: pointer;
    font-family: var(--mono-f); font-size: 11px; color: var(--muted);
    border-bottom: 2px solid transparent; margin-bottom: -1px; transition: color 0.15s;
    display: flex; align-items: center; gap: 6px;
  }
  .arch-tab.active { color: var(--primary); border-bottom-color: var(--primary); font-weight: 700; }
  .arch-tab:hover { color: var(--text); }
  .arch-canvas {
    background: var(--surface); border: 1.5px solid var(--border); border-radius: var(--radius2);
    box-shadow: var(--shadow); overflow: hidden;
  }
  .arch-panel-body { padding: 24px; }
  .arch-hero { margin-bottom: 24px; }
  .arch-hero-title { font-family: var(--display); font-size: 22px; font-weight: 700; color: var(--text); }
  .arch-hero-sub { font-size: 13px; color: var(--text2); margin-top: 4px; font-family: var(--mono-f); }
  .arch-section-title { display: flex; align-items: center; gap: 8px; margin-bottom: 14px; margin-top: 8px; }
  .arch-section-bar { width: 3px; height: 14px; border-radius: 2px; flex-shrink: 0; }
  .arch-section-label { font-size: 11px; font-weight: 700; font-family: var(--mono-f); letter-spacing: 0.8px; text-transform: uppercase; }
  .arch-badge {
    font-size: 9px; padding: 2px 6px; border-radius: 4px; border: 1px solid;
    font-family: var(--mono-f); font-weight: 600; display: inline-block;
  }
  .arch-product-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 14px; margin-bottom: 24px; }
  .arch-product-card { border: 1.5px solid; border-radius: 12px; padding: 18px; background: var(--bg); }
  .arch-product-icon { font-size: 26px; margin-bottom: 8px; }
  .arch-product-name { font-size: 14px; font-weight: 700; font-family: var(--mono-f); }
  .arch-product-sub { font-size: 10px; color: var(--muted); font-family: var(--mono-f); margin-bottom: 8px; }
  .arch-product-desc { font-size: 12px; color: var(--text2); line-height: 1.6; margin-bottom: 10px; }
  .arch-badge-row { display: flex; flex-wrap: wrap; gap: 4px; margin-bottom: 6px; }
  .arch-connector-grid { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 24px; }
  .arch-connector-card {
    display: flex; align-items: center; gap: 8px; padding: 10px 12px;
    border: 1px solid; border-radius: 8px; background: var(--bg);
  }
  .arch-connector-name { font-size: 10px; font-weight: 600; font-family: var(--mono-f); }
  .arch-tier-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 12px; }
  .arch-tier-card { border: 1.5px solid; border-radius: 8px; padding: 14px; background: var(--bg); }
  .arch-tier-name { font-size: 12px; font-weight: 700; font-family: var(--mono-f); }
  .arch-tier-price { font-size: 16px; font-weight: 800; color: var(--text); margin-top: 4px; }
  .arch-tier-sub { font-size: 10px; color: var(--muted); margin-top: 4px; font-family: var(--mono-f); }
  .arch-stack { display: flex; flex-direction: column; }
  .arch-layer { border: 1.5px solid; border-radius: 10px; padding: 14px 16px; background: var(--bg); }
  .arch-layer-label { font-size: 9px; font-weight: 700; letter-spacing: 1.5px; font-family: var(--mono-f); margin-bottom: 10px; text-transform: uppercase; }
  .arch-layer-items { display: flex; flex-wrap: wrap; gap: 8px; }
  .arch-layer-item { border: 1px solid; border-radius: 7px; padding: 8px 10px; min-width: 96px; background: var(--surface); }
  .arch-layer-icon { font-size: 15px; margin-bottom: 2px; }
  .arch-layer-name { font-size: 10px; font-weight: 700; font-family: var(--mono-f); }
  .arch-layer-sub { font-size: 9px; color: var(--muted); font-family: var(--mono-f); }
  .arch-layer-arrow { display: flex; justify-content: center; padding: 4px 0; color: var(--muted); font-size: 12px; }
  .arch-layer-arrow::before { content: "↓"; }
  .arch-mcp-layout { display: flex; align-items: flex-start; gap: 16px; flex-wrap: wrap; margin-bottom: 16px; }
  .arch-mcp-agent-box { border: 2px solid; border-radius: 12px; padding: 16px; text-align: center; width: 120px; background: var(--bg); }
  .arch-mcp-agent-icon { font-size: 22px; }
  .arch-mcp-agent-name { font-size: 11px; font-weight: 700; font-family: var(--mono-f); margin-top: 4px; }
  .arch-mcp-agent-sub { font-size: 9px; color: var(--muted); font-family: var(--mono-f); }
  .arch-mcp-arrow { color: var(--muted); font-size: 18px; align-self: center; }
  .arch-mcp-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(120px, 1fr)); gap: 8px; flex: 1; min-width: 280px; }
  .arch-mcp-card {
    border: 1px solid; border-radius: 8px; padding: 10px; cursor: pointer;
    background: var(--bg); text-align: left; transition: box-shadow 0.15s;
  }
  .arch-mcp-card.active { box-shadow: 0 0 0 2px var(--primaryL); }
  .arch-mcp-card-icon { font-size: 16px; display: block; }
  .arch-mcp-card-name { font-size: 9px; font-weight: 700; font-family: var(--mono-f); display: block; margin-top: 4px; }
  .arch-mcp-card-port { font-size: 9px; color: var(--muted); font-family: var(--mono-f); }
  .arch-mcp-detail { border: 1.5px solid; border-radius: 12px; padding: 16px; background: var(--bg); margin-top: 8px; }
  .arch-mcp-detail-head { font-size: 14px; font-weight: 700; font-family: var(--mono-f); margin-bottom: 12px; display: flex; gap: 8px; align-items: center; }
  .arch-mcp-detail-row { margin-bottom: 10px; }
  .arch-mcp-detail-label { font-size: 9px; color: var(--muted); font-family: var(--mono-f); text-transform: uppercase; display: block; margin-bottom: 4px; }
  .arch-mcp-cmd { display: block; padding: 8px 10px; background: var(--ink); color: var(--success); border-radius: 6px; font-size: 10px; font-family: var(--mono-f); }
  .arch-mcp-tools { display: flex; flex-direction: column; gap: 4px; margin-top: 8px; }
  .arch-mcp-tool { display: block; padding: 6px 10px; background: var(--surface); border: 1px solid var(--border); border-radius: 4px; font-size: 10px; font-family: var(--mono-f); }
  .arch-flow-tabs { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 16px; }
  .arch-flow-tab {
    border: 1px solid; border-radius: 8px; padding: 8px 14px; cursor: pointer;
    background: transparent; font-family: var(--mono-f); font-size: 11px; font-weight: 600;
  }
  .arch-flow-query {
    background: var(--bg); border: 1px solid; border-radius: 8px; padding: 12px 14px;
    margin-bottom: 20px; display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  }
  .arch-flow-query-label { font-size: 10px; color: var(--muted); font-family: var(--mono-f); }
  .arch-flow-query-text { font-size: 12px; font-family: var(--mono-f); font-weight: 600; }
  .arch-flow-steps { display: flex; flex-direction: column; }
  .arch-flow-step { display: flex; gap: 0; }
  .arch-flow-step-rail { width: 36px; flex-shrink: 0; display: flex; flex-direction: column; align-items: center; }
  .arch-flow-step-num {
    width: 26px; height: 26px; border-radius: 50%; border: 2px solid;
    display: flex; align-items: center; justify-content: center;
    font-size: 11px; font-weight: 700; font-family: var(--mono-f); background: var(--surface);
  }
  .arch-flow-step-line { width: 1px; flex: 1; min-height: 12px; background: var(--border); }
  .arch-flow-step-body { flex: 1; padding-left: 10px; padding-bottom: 14px; }
  .arch-flow-step-head { display: flex; align-items: center; gap: 8px; font-size: 11px; font-weight: 700; font-family: var(--mono-f); margin-bottom: 4px; }
  .arch-flow-step-detail { font-size: 11px; color: var(--text2); font-family: var(--mono-f); line-height: 1.5; padding-left: 22px; }
  .arch-zero-hall { border: 1.5px solid; border-radius: 8px; padding: 12px 14px; background: var(--successL); margin-top: 20px; }
  .arch-zero-hall-title { font-size: 10px; font-weight: 700; font-family: var(--mono-f); margin-bottom: 6px; }
  .arch-zero-hall-body { font-size: 11px; color: var(--text2); line-height: 1.55; margin: 0; }
  .arch-tree { background: var(--bg); border: 1px solid var(--border); border-radius: 10px; padding: 16px; font-family: var(--mono-f); font-size: 11px; }
  .arch-tree-root { font-weight: 700; margin-bottom: 10px; color: var(--text); }
  .arch-tree-dir { margin-bottom: 6px; }
  .arch-tree-dir-head {
    display: flex; align-items: center; gap: 8px; padding: 6px 8px; border-radius: 6px;
    border: 1px solid; background: var(--surface); cursor: pointer; width: 100%; text-align: left;
    font-family: var(--mono-f); font-size: 11px;
  }
  .arch-tree-dir-desc { color: var(--muted); font-size: 10px; }
  .arch-tree-file { display: flex; gap: 6px; padding: 3px 0 3px 20px; color: var(--text2); }
  .arch-cta-row { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
  @media (max-width: 560px) {
    .infra-log-row { grid-template-columns: 1fr; gap: 2px; }
    .infra-sev-pill { border-radius: 12px; }
    .arch-mcp-layout { flex-direction: column; }
  }
  .msg-text { line-height: 1.65; }
  .ops-structured { display: flex; flex-direction: column; gap: 10px; }
  .ops-panel, .ops-knowledge {
    background: var(--bg);
    border: 1.5px solid var(--border);
    border-radius: 11px;
    padding: 14px 16px;
  }
  .ops-panel-animate { animation: panelIn 0.35s ease both; }
  @keyframes panelIn { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
  .ops-panel-head { display: flex; align-items: flex-start; gap: 10px; margin-bottom: 12px; }
  .ops-panel-icon { font-size: 18px; line-height: 1; margin-top: 2px; }
  .ops-panel-title { font-family: var(--display); font-weight: 700; font-size: 14px; color: var(--text); }
  .ops-panel-sub { font-size: 12px; color: var(--text2); margin-top: 2px; }
  .ops-kv-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px 16px; }
  .ops-kv-grid-1 { grid-template-columns: 1fr; }
  @media (max-width: 560px) { .ops-kv-grid { grid-template-columns: 1fr; } }
  .ops-kv { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
  .ops-kv-row { flex-direction: row; align-items: center; justify-content: space-between; gap: 12px; padding: 8px 0; border-bottom: 1px solid var(--border); }
  .ops-kv-row:last-child { border-bottom: none; padding-bottom: 0; }
  .ops-kv-key { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.4px; color: var(--muted); }
  .ops-kv-val { font-size: 13px; font-weight: 500; color: var(--text); word-break: break-word; }
  .ops-logs-wrap { overflow-x: auto; margin: 0 -4px; }
  .ops-logs-table th, .ops-logs-table td { padding: 8px 10px; }
  .ops-knowledge { background: linear-gradient(180deg, var(--card) 0%, var(--bg) 100%); border-color: rgba(255,91,46,0.28); }
  .ops-knowledge-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 14px; margin-bottom: 14px; }
  .ops-knowledge-badge { display: inline-block; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.6px; color: var(--primary); background: var(--primaryL); padding: 3px 8px; border-radius: 6px; margin-bottom: 6px; }
  .ops-progress-ring {
    width: 48px; height: 48px; border-radius: 50%; flex-shrink: 0;
    background: conic-gradient(var(--primary) calc(var(--pct) * 1%), var(--border) 0);
    display: flex; align-items: center; justify-content: center;
    position: relative;
  }
  .ops-progress-ring::before { content: ''; position: absolute; inset: 5px; background: var(--surface); border-radius: 50%; }
  .ops-progress-text { position: relative; font-size: 11px; font-weight: 700; color: var(--primary); font-family: var(--display); }
  .ops-steps { display: flex; flex-direction: column; gap: 10px; }
  .ops-step { display: flex; gap: 12px; padding: 12px; border-radius: 10px; border: 1.5px solid var(--border); background: var(--surface); transition: opacity 0.2s, border-color 0.2s; }
  .ops-step.done { opacity: 0.72; border-color: var(--success); background: var(--successL); }
  .ops-step-check { display: flex; align-items: flex-start; gap: 8px; cursor: pointer; flex-shrink: 0; padding-top: 2px; }
  .ops-step-check input { position: absolute; opacity: 0; width: 0; height: 0; }
  .ops-step-num {
    width: 26px; height: 26px; border-radius: 8px;
    background: var(--primaryL); color: var(--primary);
    font-family: var(--display); font-weight: 800; font-size: 12px;
    display: flex; align-items: center; justify-content: center;
    transition: background 0.2s, color 0.2s;
  }
  .ops-step.done .ops-step-num { background: var(--success); color: #fff; }
  .ops-step-body { flex: 1; min-width: 0; }
  .ops-step-title { font-weight: 700; font-size: 13px; margin-bottom: 4px; }
  .ops-step-instruction { font-size: 13px; color: var(--text2); line-height: 1.55; }
  .ops-callout { margin-top: 8px; padding: 8px 10px; border-radius: 8px; font-size: 12px; line-height: 1.5; }
  .ops-callout-info { background: var(--primaryL); color: var(--primary); border: 1px solid rgba(255,91,46,0.28); }
  .ops-callout-warn { background: var(--warningL); color: var(--warning); border: 1px solid #FDE68A; }
  .ops-command { margin-top: 8px; padding: 10px 12px; background: #0F172A; color: #E2E8F0; border-radius: 8px; font-size: 11.5px; line-height: 1.5; overflow-x: auto; white-space: pre-wrap; word-break: break-all; }
  .ops-resources { margin-top: 14px; padding-top: 12px; border-top: 1px solid var(--border); }
  .ops-resource-link { font-size: 12px; font-weight: 600; color: var(--primary); padding: 5px 10px; border-radius: 7px; background: var(--primaryL); transition: background 0.15s; }
  .ops-resource-link:hover { background: var(--primaryL); }
  .output-section { background: var(--bg); border: 1.5px solid var(--border); border-radius: 11px; padding: 14px 16px; }
  .output-section-head { display: flex; align-items: flex-start; gap: 10px; margin-bottom: 12px; }
  .output-section-icon { font-size: 18px; line-height: 1; margin-top: 1px; }
  .output-section-title { font-family: var(--display); font-weight: 700; font-size: 14px; color: var(--text); }
  .output-section-sub { font-size: 12px; color: var(--text2); margin-top: 2px; }
  .sources-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 8px; }
  .sources-item { display: flex; align-items: center; gap: 10px; padding: 10px 12px; border-radius: 9px; background: var(--surface); border: 1px solid var(--border); }
  .sources-num { width: 22px; height: 22px; border-radius: 6px; background: var(--primaryL); color: var(--primary); font-size: 11px; font-weight: 700; display: grid; place-items: center; flex-shrink: 0; font-family: var(--mono-f); }
  .sources-label { flex: 1; min-width: 0; font-size: 13px; font-weight: 500; color: var(--text); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .sources-score { font-size: 11px; color: var(--muted); flex-shrink: 0; }
  .ops-detail-card { background: var(--surface); border: 1px solid var(--border); border-radius: 9px; overflow: hidden; }
  .ops-detail-row { display: grid; grid-template-columns: minmax(120px, 38%) 1fr; gap: 12px; padding: 10px 14px; border-bottom: 1px solid var(--border); align-items: start; }
  .ops-detail-row:last-child { border-bottom: none; }
  .ops-detail-key { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px; color: var(--muted); }
  .ops-detail-val { font-size: 13px; font-weight: 500; color: var(--text); word-break: break-word; }
  @media (max-width: 560px) { .ops-detail-row { grid-template-columns: 1fr; gap: 4px; } }
  .ops-sources { margin-top: 0; }
  /* ── Upgrade activity panels ── */
  .ops-upgrade-overview {
    background: linear-gradient(135deg, var(--card) 0%, var(--primaryL) 100%);
    border: 1.5px solid rgba(255,91,46,0.28);
    border-radius: 11px;
    padding: 14px 16px;
  }
  .ops-upgrade-overview-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; margin-bottom: 12px; }
  .ops-upgrade-version { display: flex; align-items: center; gap: 10px; margin-bottom: 14px; flex-wrap: wrap; }
  .ops-upgrade-from, .ops-upgrade-to { font-size: 15px; font-weight: 700; color: var(--text); padding: 4px 10px; background: var(--surface); border-radius: 8px; border: 1px solid var(--border); }
  .ops-upgrade-to { color: var(--primary); border-color: rgba(255,91,46,0.28); background: var(--primaryL); }
  .ops-upgrade-arrow { font-size: 18px; font-weight: 800; color: var(--primary); }
  .ops-upgrade-kpi-strip { display: grid; grid-template-columns: repeat(auto-fill, minmax(120px, 1fr)); gap: 10px 14px; }
  .ops-upgrade-kpi { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
  .ops-upgrade-kpi-wide { grid-column: 1 / -1; }
  .ops-upgrade-kpi-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: var(--muted); }
  .ops-upgrade-kpi-val { font-size: 13px; font-weight: 600; color: var(--text); }
  .ops-upgrade-services { margin-top: 12px; padding-top: 12px; border-top: 1px dashed rgba(255,91,46,0.28); }

  /* ── Platform INPUT / OUTPUT screen (config guides) ── */
  .plat-io-screen {
    background: linear-gradient(165deg, #fffdf8 0%, var(--surface) 55%, #f4f1ea 100%);
    border: 1.5px solid rgba(198, 97, 63, 0.22);
    border-radius: 14px;
    padding: 18px 20px 20px;
    margin-bottom: 14px;
    box-shadow: 0 8px 28px rgba(14, 17, 22, 0.06);
  }
  .plat-io-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 14px; margin-bottom: 12px; }
  .plat-io-eyebrow { font-size: 10px; font-weight: 800; letter-spacing: 0.14em; text-transform: uppercase; color: var(--primary); margin-bottom: 4px; }
  .plat-io-title { font-family: var(--display); font-size: 20px; font-weight: 700; color: var(--text); margin: 0 0 6px; line-height: 1.25; }
  .plat-io-sub { font-size: 13px; color: var(--text2); line-height: 1.5; margin: 0; max-width: 52rem; }
  .plat-io-platform-pill {
    flex-shrink: 0; padding: 6px 14px; border-radius: 999px; font-size: 12px; font-weight: 700;
    background: var(--primaryL); color: var(--primary); border: 1.5px solid rgba(198, 97, 63, 0.35);
  }
  .plat-io-meta-row {
    display: flex; flex-wrap: wrap; gap: 10px 18px; font-size: 12px; color: var(--text2);
    padding: 10px 12px; background: rgba(255,255,255,0.65); border-radius: 10px; border: 1px solid var(--border);
    margin-bottom: 16px;
  }
  .plat-io-meta-row strong { color: var(--muted); font-weight: 700; margin-right: 4px; text-transform: uppercase; font-size: 10px; letter-spacing: 0.04em; }
  .plat-io-meta-wide { flex: 1 1 100%; }
  .plat-io-columns { display: grid; grid-template-columns: 1fr auto 1fr; gap: 0; align-items: stretch; }
  @media (max-width: 900px) { .plat-io-columns { grid-template-columns: 1fr; gap: 12px; } .plat-io-divider { display: none; } }
  .plat-io-col { border-radius: 12px; padding: 14px; min-width: 0; }
  .plat-io-col-input {
    background: linear-gradient(180deg, rgba(43, 76, 255, 0.06) 0%, rgba(255,255,255,0.9) 100%);
    border: 1.5px solid rgba(43, 76, 255, 0.18);
  }
  .plat-io-col-output {
    background: linear-gradient(180deg, rgba(198, 97, 63, 0.08) 0%, rgba(255,255,255,0.92) 100%);
    border: 1.5px solid rgba(198, 97, 63, 0.28);
  }
  .plat-io-col-label { font-size: 12px; font-weight: 600; color: var(--text2); margin-bottom: 12px; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  .plat-io-col-badge { font-size: 10px; font-weight: 800; letter-spacing: 0.12em; padding: 3px 8px; border-radius: 6px; }
  .plat-io-badge-in { background: rgba(43, 76, 255, 0.12); color: #2b4cff; }
  .plat-io-badge-out { background: var(--primaryL); color: var(--primary); }
  .plat-io-divider { display: flex; align-items: center; justify-content: center; padding: 0 8px; color: var(--primary); font-size: 22px; font-weight: 800; }
  .plat-io-table-wrap { margin-bottom: 12px; }
  .plat-io-table-title { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: var(--muted); margin-bottom: 6px; }
  .plat-io-table { width: 100%; border-collapse: collapse; font-size: 12px; }
  .plat-io-table th { text-align: left; padding: 6px 8px; border-bottom: 1.5px solid var(--border); color: var(--muted); font-weight: 700; }
  .plat-io-table td { padding: 7px 8px; border-bottom: 1px solid rgba(14,17,22,0.06); vertical-align: top; }
  .plat-io-param { color: #2b4cff; font-weight: 600; }
  .plat-io-empty { font-size: 13px; color: var(--muted); line-height: 1.55; padding: 12px; background: rgba(255,255,255,0.7); border-radius: 8px; border: 1px dashed var(--border); }
  .plat-io-analysis { border-radius: 10px; padding: 10px 12px; margin-bottom: 10px; font-size: 13px; line-height: 1.5; }
  .plat-io-analysis ul { margin: 6px 0 0; padding-left: 18px; }
  .plat-io-analysis-ok { background: rgba(34, 197, 94, 0.1); border: 1px solid rgba(34, 197, 94, 0.35); }
  .plat-io-analysis-warn { background: rgba(245, 158, 11, 0.12); border: 1px solid rgba(245, 158, 11, 0.4); }
  .plat-io-analysis-head { font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 4px; }
  .plat-io-gap-note { display: block; font-size: 11px; color: var(--muted); margin-top: 2px; }
  .plat-io-output-flow { margin-top: 8px; padding-top: 10px; border-top: 1px dashed rgba(198, 97, 63, 0.25); }
  .plat-io-flow-title { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: var(--primary); margin-bottom: 8px; }
  .plat-io-flow-list { margin: 0; padding-left: 18px; font-size: 12px; color: var(--text2); }
  .plat-io-flow-list li { margin-bottom: 8px; }
  .plat-io-flow-list strong { display: block; color: var(--text); font-size: 13px; }

  .ops-config {
    background: var(--surface);
    border: 1.5px solid var(--border);
    border-radius: 11px;
    padding: 14px 16px;
  }
  .ops-config-step { background: var(--bg); }
  .itsm-change-detail { border-color: rgba(255,91,46,0.28); background: linear-gradient(180deg, var(--card) 0%, var(--surface) 100%); }
  .itsm-row-selected td { background: var(--primaryL) !important; }
  .chat-suggestions { display: flex; flex-wrap: wrap; gap: 8px; padding: 0 16px 8px; }
  .chat-chip {
    font-size: 12px; font-weight: 600; color: var(--primary);
    background: var(--primaryL); border: 1.5px solid rgba(255,91,46,0.28);
    padding: 6px 12px; border-radius: 99px; cursor: pointer;
    transition: transform 0.15s, box-shadow 0.15s;
  }
  .chat-chip:hover { transform: translateY(-1px); box-shadow: 0 2px 8px rgba(255,91,46,0.14); }
  .chat-chip-nav { background: linear-gradient(135deg, var(--primaryL) 0%, var(--card) 100%); border-color: rgba(255,91,46,0.28); font-weight: 700; }
  /* ── K8s teaching panels ── */
  .k8s-overview {
    background: linear-gradient(135deg, var(--card) 0%, var(--primaryL) 100%);
    border: 1.5px solid rgba(255,91,46,0.28);
    border-radius: 11px;
    padding: 14px 16px;
    margin-bottom: 12px;
  }
  .k8s-meta-row { display: flex; flex-wrap: wrap; gap: 8px; margin: 10px 0; align-items: center; }
  .k8s-teaching-strip { display: flex; flex-wrap: wrap; align-items: stretch; gap: 4px; margin-top: 14px; }
  .k8s-teaching-node { display: flex; align-items: center; gap: 4px; }
  .k8s-teaching-arrow { color: var(--primary); font-weight: 800; font-size: 14px; padding: 0 2px; }
  .k8s-teaching-card {
    background: var(--surface); border: 1px solid var(--border); border-radius: 10px;
    padding: 10px 12px; min-width: 88px; text-align: center;
  }
  .k8s-teaching-icon { font-size: 20px; display: block; margin-bottom: 4px; }
  .k8s-teaching-label { font-size: 11px; font-weight: 700; color: var(--text); }
  .k8s-teaching-desc { font-size: 10px; color: var(--muted); margin-top: 2px; line-height: 1.3; }
  .k8s-paths { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 10px; margin: 12px 0; }
  .k8s-path-card { background: var(--surface); border: 1px solid var(--border); border-radius: 10px; padding: 12px; }
  .k8s-comp-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 10px; margin-top: 12px; }
  .k8s-comp-card { background: var(--surface); border: 1px solid var(--border); border-radius: 10px; padding: 12px; }
  .k8s-comp-head { display: flex; gap: 10px; align-items: flex-start; margin-bottom: 8px; }
  .k8s-comp-name { font-size: 13px; font-weight: 700; }
  .k8s-comp-port { font-size: 10px; color: var(--muted); }
  .k8s-comp-body { font-size: 12px; color: var(--text2); line-height: 1.55; margin-bottom: 8px; }
  .k8s-comp-tags { display: flex; flex-wrap: wrap; gap: 4px; }
  .k8s-concept { background: var(--surface); border: 1.5px solid var(--border); border-radius: 11px; padding: 14px 16px; margin-bottom: 12px; }
  .k8s-concept-block { margin-top: 12px; }
  .k8s-concept-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: var(--primary); margin-bottom: 6px; }
  .k8s-related-chips { margin-top: 12px; padding-top: 12px; border-top: 1px dashed var(--border); }
  .k8s-learn-hero { background: linear-gradient(135deg, var(--card) 0%, var(--primaryL) 100%); border: 1.5px solid rgba(255,91,46,0.28); border-radius: var(--radius2); padding: 20px; margin-bottom: 18px; overflow-x: auto; }
  .k8s-learn-flow { margin-top: 0; flex-wrap: nowrap; min-width: min-content; }
  .k8s-learn-tabs { display: flex; gap: 8px; margin-bottom: 16px; flex-wrap: wrap; }
  .k8s-learn-tab { font-size: 13px; font-weight: 600; padding: 8px 14px; border-radius: 99px; border: 1.5px solid var(--border); background: var(--surface); cursor: pointer; transition: all 0.15s; }
  .k8s-learn-tab.active { background: var(--primary); color: #fff; border-color: var(--primary); }
  .k8s-learn-split { display: grid; grid-template-columns: 260px 1fr; gap: 16px; min-height: 420px; }
  @media (max-width: 768px) { .k8s-learn-split { grid-template-columns: 1fr; } }
  .k8s-learn-list { display: flex; flex-direction: column; gap: 4px; background: var(--surface); border: 1.5px solid var(--border); border-radius: var(--radius2); padding: 8px; max-height: 520px; overflow-y: auto; }
  .k8s-learn-list-item { display: flex; gap: 10px; align-items: center; width: 100%; text-align: left; padding: 10px 12px; border: none; background: none; border-radius: 8px; cursor: pointer; transition: background 0.15s; }
  .k8s-learn-list-item:hover { background: var(--primaryL); }
  .k8s-learn-list-item.active { background: var(--primaryL); border-left: 3px solid var(--primary); }
  .k8s-learn-detail { background: var(--surface); border: 1.5px solid var(--border); border-radius: var(--radius2); padding: 20px; box-shadow: var(--shadow); }
  .k8s-learn-detail-head { display: flex; gap: 14px; align-items: center; margin-bottom: 14px; padding-bottom: 14px; border-bottom: 1px solid var(--border); }
  .k8s-learn-detail-icon { font-size: 32px; }
  .k8s-learn-install-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 14px; }
  .k8s-learn-install-card { background: var(--surface); border: 1.5px solid var(--border); border-radius: var(--radius2); padding: 18px; box-shadow: var(--shadow); }
  .topics-learn-search-wrap { display: flex; align-items: center; gap: 10px; background: var(--surface); border: 1.5px solid var(--border); border-radius: var(--radius2); padding: 10px 14px; margin-bottom: 16px; }
  .topics-learn-search { flex: 1; border: none; background: transparent; font-size: 14px; color: var(--text); outline: none; }
  .topics-learn-layout { display: grid; grid-template-columns: 240px 1fr; gap: 16px; min-height: 480px; }
  @media (max-width: 768px) { .topics-learn-layout { grid-template-columns: 1fr; } }
  .topics-learn-sidebar { display: flex; flex-direction: column; gap: 4px; background: var(--surface); border: 1.5px solid var(--border); border-radius: var(--radius2); padding: 10px; max-height: 640px; overflow-y: auto; }
  .topics-learn-sidebar-label { padding: 4px 8px 8px; text-transform: uppercase; letter-spacing: 0.4px; }
  .topics-learn-cat { display: flex; gap: 10px; align-items: center; width: 100%; text-align: left; padding: 10px 12px; border: none; background: none; border-radius: 8px; cursor: pointer; transition: background 0.15s; }
  .topics-learn-cat:hover { background: var(--primaryL); }
  .topics-learn-cat.active { background: var(--primaryL); border-left: 3px solid var(--primary); }
  .topics-learn-cat-icon { font-size: 18px; flex-shrink: 0; }
  .topics-learn-main { min-width: 0; }
  .topics-learn-cat-banner { display: flex; gap: 14px; align-items: center; background: linear-gradient(135deg, var(--card) 0%, var(--primaryL) 100%); border: 1.5px solid rgba(255,91,46,0.28); border-radius: var(--radius2); padding: 16px 18px; margin-bottom: 14px; }
  .topics-learn-cat-banner-icon { font-size: 28px; }
  .topics-learn-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 14px; }
  .topics-learn-card { background: var(--surface); border: 1.5px solid var(--border); border-radius: var(--radius2); padding: 16px; box-shadow: var(--shadow); display: flex; flex-direction: column; gap: 8px; }
  .topics-learn-card-head { display: flex; flex-direction: column; gap: 8px; }
  .topics-learn-tags { display: flex; flex-wrap: wrap; gap: 6px; }
  .topics-learn-explanation { line-height: 1.6; margin: 0; flex: 1; }
  .topics-learn-prompt { opacity: 0.85; margin-top: 4px; }
  .topics-learn-card-actions { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 4px; }
  .topics-learn-empty { padding: 24px; text-align: center; }
  .ops-glance { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 18px; }
  @media (max-width: 768px) { .ops-glance { grid-template-columns: 1fr; } }
  .ops-glance-item {
    display: flex; align-items: center; gap: 12px;
    padding: 14px 16px; background: var(--surface);
    border: 1.5px solid var(--border); border-radius: var(--radius2);
    box-shadow: var(--shadow); transition: border-color 0.2s;
  }
  .ops-glance-item:hover { border-color: var(--primary); }
  .ops-glance-icon { width: 40px; height: 40px; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 18px; background: var(--primaryL); flex-shrink: 0; }
  .msg-bubble.wide { max-width: min(900px, 100%); width: 100%; }
  .msg.assistant-wide { max-width: 960px; width: 100%; }

  /* ── Loggly-style logs explorer ── */
  .lgly { display: flex; flex-direction: column; background: var(--surface); border: 1.5px solid var(--border); border-radius: var(--radius2); overflow: hidden; box-shadow: var(--shadow); }
  .lgly.embedded { border-radius: 11px; }
  .lgly-tabs { display: flex; align-items: stretch; gap: 0; background: var(--bg-2); border-bottom: 1px solid var(--border); padding: 0 8px 0 12px; }
  .lgly-tab { font-size: 12px; font-weight: 600; padding: 10px 14px; color: var(--text2); border-bottom: 2px solid transparent; margin-bottom: -1px; transition: all 0.15s; }
  .lgly-tab:hover { color: var(--primary); }
  .lgly-tab.active { background: var(--surface); color: var(--primary); border-bottom-color: var(--primary); border-radius: 6px 6px 0 0; }
  .lgly-tab-new { font-size: 16px; font-weight: 700; color: var(--muted); padding: 8px 12px; }
  .lgly-search-bar { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; padding: 12px 14px; background: var(--surface); border-bottom: 1px solid var(--border); }
  .lgly-source, .lgly-timerange { font-size: 12px; padding: 8px 10px; border: 1.5px solid var(--border); border-radius: 6px; background: var(--bg); color: var(--text); }
  .lgly-query { flex: 1; min-width: 180px; font-size: 13px; padding: 8px 12px; border: 1.5px solid var(--border); border-radius: 6px; background: #fff; color: var(--text); }
  .lgly-query:focus { border-color: var(--primary); outline: none; }
  .lgly-search-btn { font-size: 13px; font-weight: 700; padding: 8px 18px; border-radius: 6px; background: var(--primary); color: #fff; box-shadow: 0 2px 6px rgba(255,91,46,0.22); transition: background 0.15s; }
  .lgly-search-btn:hover { background: var(--primary2); }
  .lgly-main { display: flex; min-height: 520px; background: var(--bg); }
  .lgly-main.compact { min-height: 360px; flex-direction: column; }
  .lgly-field-explorer { width: 240px; flex-shrink: 0; background: #F0F3F8; border-right: 1px solid var(--border); display: flex; flex-direction: column; overflow: hidden; }
  .lgly-fe-head { display: flex; align-items: center; justify-content: space-between; padding: 12px 12px 8px; border-bottom: 1px solid var(--border); background: #E8ECF3; }
  .lgly-fe-title { font-size: 12px; font-weight: 700; color: var(--text); }
  .lgly-fe-icon { font-size: 14px; color: var(--muted); }
  .lgly-fe-body { display: flex; flex: 1; min-height: 0; overflow: hidden; }
  .lgly-fe-fields { width: 96px; border-right: 1px solid var(--border); overflow-y: auto; padding: 6px 0; }
  .lgly-fe-field { display: flex; align-items: center; gap: 4px; width: 100%; text-align: left; padding: 7px 10px; font-size: 11px; font-weight: 600; color: var(--text2); }
  .lgly-fe-field:hover { background: var(--primaryL); color: var(--primary); }
  .lgly-fe-field.active { background: var(--surface); color: var(--primary); border-left: 2px solid var(--primary); }
  .lgly-fe-folder { font-size: 9px; opacity: 0.6; }
  .lgly-fe-values { flex: 1; overflow-y: auto; padding: 6px 0; background: var(--surface); }
  .lgly-fe-values-head { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px; color: var(--muted); padding: 6px 10px 8px; }
  .lgly-fe-value { display: flex; flex-direction: column; align-items: flex-start; gap: 2px; width: 100%; padding: 6px 10px; text-align: left; border: none; background: none; cursor: pointer; }
  .lgly-fe-value:hover { background: var(--primaryL); }
  .lgly-fe-value.selected { background: var(--primaryL); }
  .lgly-fe-value-name { font-family: var(--mono-f); font-size: 11px; font-weight: 600; color: var(--text); max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .lgly-fe-value-count { font-size: 10px; color: var(--muted); }
  .lgly-fe-active { display: flex; flex-wrap: wrap; gap: 6px; padding: 8px 10px; border-top: 1px solid var(--border); }
  .lgly-chip { font-size: 10px; font-weight: 600; padding: 3px 8px; border-radius: 4px; background: var(--primaryL); color: var(--primary); border: 1px solid rgba(255,91,46,0.28); }
  .lgly-center { flex: 1; display: flex; flex-direction: column; min-width: 0; background: var(--surface); }
  .lgly-center-tabs { display: flex; gap: 0; padding: 0 14px; border-bottom: 1px solid var(--border); background: var(--bg); }
  .lgly-center-tab { font-size: 12px; font-weight: 700; padding: 10px 14px; color: var(--text2); border-bottom: 2px solid transparent; margin-bottom: -1px; }
  .lgly-center-tab.active { color: var(--primary); border-bottom-color: var(--primary); background: var(--surface); }
  .lgly-timeline { padding: 12px 16px 10px; border-bottom: 1px solid var(--border); }
  .lgly-timeline-meta { display: flex; justify-content: space-between; align-items: baseline; flex-wrap: wrap; gap: 8px; margin-bottom: 8px; }
  .lgly-event-count { font-family: var(--display); font-weight: 800; font-size: 18px; color: var(--text); }
  .lgly-event-range { font-size: 11px; color: var(--muted); }
  .lgly-timeline-svg { width: 100%; height: 150px; display: block; }
  .lgly.embedded .lgly-timeline-svg { height: 110px; }
  .lgly-grid { stroke: var(--border); stroke-width: 1; }
  .lgly-axis-label { fill: var(--muted); font-size: 9px; font-family: var(--mono-f); }
  .lgly-legend { display: flex; flex-wrap: wrap; gap: 8px 14px; margin-top: 8px; justify-content: center; }
  .lgly-legend-item { display: flex; align-items: center; gap: 5px; font-size: 11px; font-weight: 600; color: var(--text2); }
  .lgly-legend-swatch { width: 10px; height: 10px; border-radius: 2px; flex-shrink: 0; }
  .lgly-chart-placeholder { padding: 40px; text-align: center; }
  .lgly-event-view { flex: 1; display: flex; flex-direction: column; min-height: 0; }
  .lgly-event-toolbar { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; padding: 8px 14px; border-bottom: 1px solid var(--border); background: var(--bg); }
  .lgly-ev-title { font-size: 12px; font-weight: 700; color: var(--text); margin-right: 4px; }
  .lgly-tool-btn { font-size: 11px; font-weight: 600; padding: 4px 10px; border-radius: 5px; border: 1px solid var(--border); background: var(--surface); color: var(--text2); }
  .lgly-tool-btn:hover { border-color: var(--primary); color: var(--primary); }
  .lgly-ev-count { margin-left: auto; font-size: 11px; color: var(--muted); font-weight: 600; }
  .lgly-event-list { flex: 1; overflow-y: auto; max-height: 380px; }
  .lgly.embedded .lgly-event-list { max-height: 220px; }
  .lgly-event { padding: 8px 14px; border-bottom: 1px solid var(--border); cursor: pointer; line-height: 1.55; transition: background 0.12s; }
  .lgly-event:hover { background: #F5F8FF; }
  .lgly-event.expanded { background: var(--primaryL); }
  .lgly-event-ts { font-size: 11px; font-weight: 700; color: var(--text); margin-right: 8px; white-space: nowrap; }
  .lgly-json-badge { display: inline-block; font-size: 10px; font-weight: 700; color: #fff; background: var(--primary); padding: 1px 6px; border-radius: 3px; margin-right: 8px; vertical-align: middle; }
  .lgly-event-body { font-size: 11.5px; color: var(--text2); word-break: break-word; }
  .lgly-key { color: var(--primary); font-weight: 600; }
  .lgly-val { color: var(--text); }
  .lgly-event-extra { margin-top: 8px; padding: 8px 10px; background: var(--surface); border: 1px solid var(--border); border-radius: 6px; font-size: 11px; line-height: 1.6; }
  .lgly-empty { padding: 28px; text-align: center; color: var(--muted); font-size: 13px; }
  .lgly-domain-bar { display: grid; grid-template-columns: repeat(4, 1fr); gap: 0; border-bottom: 1px solid var(--border); background: var(--bg); }
  .lgly-domain-tab { display: flex; flex-direction: column; align-items: flex-start; gap: 2px; padding: 12px 14px; text-align: left; border-right: 1px solid var(--border); transition: background 0.15s; }
  .lgly-domain-tab:last-child { border-right: none; }
  .lgly-domain-tab:hover { background: var(--primaryL); }
  .lgly-domain-tab.active { background: var(--surface); box-shadow: inset 0 -2px 0 var(--primary); }
  .lgly-domain-icon { font-size: 16px; }
  .lgly-domain-label { font-size: 13px; font-weight: 700; color: var(--text); }
  .lgly-domain-hint { font-size: 10px; color: var(--muted); }
  .lgly-sre-strip { display: flex; flex-wrap: wrap; gap: 10px; padding: 12px 14px; background: #FEF2F2; border-bottom: 1px solid #FECACA; }
  .lgly-sre-metric { min-width: 120px; padding: 8px 12px; background: var(--surface); border: 1px solid var(--border); border-radius: 8px; }
  .lgly-sre-metric-wide { flex: 1; min-width: 180px; }
  .lgly-sre-label { display: block; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px; color: var(--muted); margin-bottom: 4px; }
  .lgly-sre-value { font-family: var(--display); font-weight: 800; font-size: 18px; color: var(--text); }
  .lgly-sre-sub { display: block; font-size: 10px; color: var(--muted); margin-top: 2px; }
  .lgly-sre-chips { display: flex; flex-wrap: wrap; gap: 6px; }
  .lgly-chip-danger { background: #FEE2E2; color: #B91C1C; border-color: #FECACA; }
  .lgly-sre-sev { display: flex; flex-wrap: wrap; gap: 8px; font-size: 11px; font-weight: 700; }
  .lgly-sev-critical { color: #B91C1C; }
  .lgly-sev-high { color: #EA580C; }
  .lgly-sev-medium { color: #D97706; }
  .ops-logs-link-row { margin-bottom: 8px; }
  @media (max-width: 900px) {
    .lgly-domain-bar { grid-template-columns: repeat(2, 1fr); }
  }
    .lgly-main:not(.compact) { flex-direction: column; }
    .lgly-field-explorer { width: 100%; max-height: 200px; border-right: none; border-bottom: 1px solid var(--border); }
  }

  .ops-glance-clickable { cursor: pointer; }
  .ops-glance-clickable:hover { border-color: var(--primary); transform: translateY(-1px); }
  .ops-glance-spark { display: flex; align-items: flex-end; gap: 2px; height: 28px; margin-top: 6px; }
  .ops-glance-spark-bar { flex: 1; border-radius: 2px 2px 0 0; min-height: 3px; background: linear-gradient(180deg, var(--danger), #F87171); opacity: 0.8; }

  /* ── Operations home (dashboard) ── */
  .dash-hero {
    position: relative; overflow: hidden;
    display: flex; flex-wrap: wrap; align-items: flex-start; justify-content: space-between; gap: 16px;
    padding: 22px 24px; margin-bottom: 16px; border-radius: var(--radius2);
    border: 1px solid rgba(198,97,63,0.12);
    background: linear-gradient(118deg, #fff9f5 0%, #f7f7f4 38%, #e8f5ef 70%, #eef4ff 100%);
    box-shadow: 0 4px 24px rgba(28,28,28,0.06);
  }
  .dash-hero::before {
    content: ""; position: absolute; inset: 0; pointer-events: none;
    background: linear-gradient(90deg, rgba(198,97,63,0.09) 0%, transparent 32%, transparent 58%, rgba(15,122,85,0.11) 88%, rgba(59,130,246,0.08) 100%);
  }
  .dash-hero-text, .dash-hero-meta { position: relative; z-index: 1; }
  .dash-hero-eyebrow {
    font-family: var(--mono-f); font-size: 10px; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase;
    color: var(--primary); margin-bottom: 6px;
  }
  .dash-hero-title { font-family: var(--ui-display); font-size: clamp(22px, 3vw, 28px); line-height: 1.15; margin-bottom: 8px; color: var(--text); font-weight: 600; }
  .dash-hero-title em { font-style: italic; color: var(--primary); }
  .dash-hero-sub { font-size: 14px; line-height: 1.6; color: var(--text2); max-width: 520px; }
  .dash-hero-meta { display: flex; flex-wrap: wrap; gap: 10px; }
  .dash-hero-stat {
    min-width: 96px; padding: 10px 12px; border-radius: 11px;
    background: rgba(255,255,255,0.72); border: 1px solid rgba(0,0,0,0.06);
    backdrop-filter: blur(6px);
  }
  .dash-hero-stat-label { display: block; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: var(--muted); margin-bottom: 4px; }
  .dash-hero-stat-value { font-family: var(--ui-display); font-size: 17px; font-weight: 700; }
  .dash-hero-stat-value.ok { color: var(--success); }
  .dash-hero-stat-value.warn { color: var(--warning); }
  .dash-kpi-strip { margin-bottom: 16px; }
  .dash-section { margin-bottom: 16px; }
  .dash-section-title {
    font-family: var(--ui-display); font-size: 13px; font-weight: 600; letter-spacing: 0.02em;
    color: var(--text2); margin-bottom: 10px;
  }
  .dash-actions-grid {
    display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px;
  }
  @media (max-width: 900px) { .dash-actions-grid { grid-template-columns: repeat(2, 1fr); } }
  @media (max-width: 520px) { .dash-actions-grid { grid-template-columns: 1fr; } }
  .dash-action-card {
    text-align: left; padding: 14px 16px; border-radius: 12px;
    border: 1px solid var(--border); background: var(--card);
    display: flex; flex-direction: column; gap: 8px; transition: border-color 0.15s, transform 0.15s, box-shadow 0.15s;
    box-shadow: 0 1px 3px rgba(28,28,28,0.04);
  }
  .dash-action-card:hover { transform: translateY(-2px); box-shadow: 0 10px 28px rgba(28,28,28,0.08); }
  .dash-action-card.dash-accent-terra:hover { border-color: rgba(198,97,63,0.45); }
  .dash-action-card.dash-accent-teal:hover { border-color: rgba(15,122,85,0.45); }
  .dash-action-card.dash-accent-blue:hover { border-color: rgba(37,99,235,0.4); }
  .dash-action-card.dash-accent-plum:hover { border-color: rgba(124,58,237,0.35); }
  .dash-action-primary {
    border-color: rgba(198,97,63,0.28);
    background: linear-gradient(165deg, #fff9f5 0%, #fffdf8 100%);
  }
  .dash-action-icon-wrap {
    width: 38px; height: 38px; border-radius: 10px; display: grid; place-items: center; font-size: 18px;
  }
  .dash-accent-terra .dash-action-icon-wrap { background: var(--primaryL); }
  .dash-accent-teal .dash-action-icon-wrap { background: var(--accentL); }
  .dash-accent-blue .dash-action-icon-wrap { background: #eff6ff; }
  .dash-accent-plum .dash-action-icon-wrap { background: var(--plumL); }
  .dash-action-title { font-family: var(--ui-display); font-weight: 600; font-size: 14px; color: var(--text); }
  .dash-action-desc { font-size: 12px; color: var(--text2); line-height: 1.45; }
  .dash-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
  @media (max-width: 768px) { .dash-grid-2 { grid-template-columns: 1fr; } }
  .dash-grid-mt { margin-top: 14px; }
  .dash-card { height: 100%; }
  .dash-log-pills { display: flex; flex-direction: column; gap: 10px; }
  .dash-log-pill {
    display: flex; align-items: flex-start; gap: 10px; padding: 10px 12px; border-radius: 10px;
    border: 1.5px solid var(--border); background: var(--bg);
  }
  .dash-log-pill-ok { border-color: rgba(15,122,85,0.25); background: var(--successL); }
  .dash-log-pill-off { opacity: 0.92; }
  .dash-log-pill-dot { font-weight: 800; font-size: 13px; color: var(--success); min-width: 18px; }
  .dash-log-pill-off .dash-log-pill-dot { color: var(--muted); }
  .dash-checklist-compact { margin: 0; padding-left: 18px; display: flex; flex-direction: column; gap: 8px; line-height: 1.45; }
  .dash-kb-row { display: flex; align-items: center; gap: 12px; padding: 10px 0; border-bottom: 1px solid var(--border); }
  .dash-kb-row:last-child { border-bottom: none; }
  .dash-status-row {
    display: flex; align-items: center; justify-content: space-between; gap: 12px;
    padding: 10px 0; border-bottom: 1px solid var(--border);
  }
  .dash-status-row:last-child { border-bottom: none; }

  /* ── Page header ── */
  .console-page { animation: pageIn 0.25s ease both; }
  @keyframes pageIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
  .page-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 22px; gap: 16px; flex-wrap: wrap; }
  .page-header-main { min-width: 0; flex: 1; }
  .page-title-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
  .page-title { font-family: var(--display); font-weight: 800; font-size: 20px; color: var(--text); margin: 0; }
  .page-sub { font-size: 13px; color: var(--text2); margin: 6px 0 0; line-height: 1.45; }
  .page-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; flex-shrink: 0; }
  .page-badge { font-family: var(--mono-f); font-size: 10px; font-weight: 600; letter-spacing: 0.6px; text-transform: uppercase; padding: 3px 9px; border-radius: 999px; border: 1px solid transparent; }
  .page-badge-live { color: var(--success); background: var(--successL); border-color: #BBF7D0; }
  .page-badge-demo { color: var(--warning); background: var(--warningL); border-color: #FDE68A; }
  .page-sync { font-size: 11px; color: var(--muted); font-weight: 600; }

  /* ── Mobile bottom nav ── */
  .bottom-nav {
    display: none;
    position: fixed; bottom: 0; left: 0; right: 0; z-index: 100;
    background: var(--surface); border-top: 1.5px solid var(--border);
    padding: 8px 0 env(safe-area-inset-bottom, 8px);
    box-shadow: 0 -4px 16px rgba(0,0,0,0.08);
  }
  .bottom-nav-inner { display: flex; justify-content: space-around; }
  .bn-item { display: flex; flex-direction: column; align-items: center; gap: 3px; padding: 4px 8px; border-radius: 10px; cursor: pointer; min-width: 56px; }
  .bn-item.active { color: var(--primary); }
  .bn-item:not(.active) { color: var(--muted); }
  .bn-icon { font-size: 20px; }
  .bn-label { font-size: 10px; font-weight: 600; }
  @media (max-width: 768px) {
    .bottom-nav { display: block; }
    .content { padding-bottom: 76px; }
  }
  @media (max-width: 480px) {
    .content { padding-bottom: 84px; }
  }

  /* ── ITSM (adapted from Veltacore patterns) ── */
  .itsm-row-click { cursor: pointer; }
  .itsm-row-click:hover td { background: #F5F8FF !important; }
  .itsm-sla-bar { height: 8px; background: var(--border); border-radius: 99px; overflow: hidden; }
  .itsm-sla-fill { height: 100%; border-radius: 99px; transition: width 0.5s ease; }
  .itsm-kv-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px 20px; }
  @media (max-width: 560px) { .itsm-kv-grid { grid-template-columns: 1fr; } }
  .itsm-kv-k { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: var(--muted); margin-bottom: 3px; }
  .itsm-kv-v { font-size: 13px; font-weight: 600; }
  .itsm-timeline-row { display: flex; align-items: flex-start; gap: 10px; padding: 10px 0; border-bottom: 1px solid var(--border); }
  .itsm-timeline-row:last-child { border-bottom: none; }
  .itsm-ci-active { border-color: var(--primary) !important; box-shadow: 0 0 0 1px var(--primary); }
  .itsm-ctl { position: relative; padding-top: 4px; overflow: hidden; }
  .itsm-ctl-axis { position: relative; height: 16px; margin-bottom: 8px; margin-left: 92px; margin-right: 8px; }
  .itsm-ctl-axis span { position: absolute; transform: translateX(-50%); font-size: 10px; color: var(--muted); font-family: var(--mono-f); }
  .itsm-ctl-grid { position: relative; }
  .itsm-ctl-lane { display: grid; grid-template-columns: 92px 1fr; align-items: center; height: 32px; margin-bottom: 6px; }
  .itsm-ctl-lane-label { font-size: 11px; color: var(--text2); font-weight: 600; padding-right: 12px; }
  .itsm-ctl-lane-track { position: relative; height: 26px; background: var(--bg); border-radius: 6px; border: 1px solid var(--border); }
  .itsm-ctl-event { position: absolute; top: 3px; bottom: 3px; padding: 0 8px; border: 1px solid var(--primary); border-radius: 4px; display: flex; align-items: center; font-size: 10px; font-weight: 600; color: var(--primary); background: var(--primaryL); min-width: 8px; white-space: nowrap; overflow: hidden; }
  .itsm-ctl-event.emergency { border-color: var(--danger); color: var(--danger); background: var(--dangerL); }
  .itsm-ctl-now { position: absolute; top: -4px; bottom: -4px; width: 0; border-left: 1.5px dashed var(--danger); pointer-events: none; z-index: 2; }
  .itsm-ctl-now span { position: absolute; top: -2px; left: 4px; font-size: 9px; font-weight: 700; color: var(--danger); background: var(--surface); padding: 0 4px; border-radius: 3px; font-family: var(--mono-f); }
  .lgly-incident-link { margin-top: 8px; }

  /* ── L2 enterprise knowledge chat ── */
  .chat-auth-banner {
    display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap;
    padding: 12px 16px; margin: 0;
    border-bottom: 1px solid #FDE68A;
    background: var(--warningL);
    font-size: 13px; color: var(--text2);
  }
  .chat-auth-banner strong { color: var(--text); }
  .chat-auth-actions { display: flex; gap: 8px; flex-wrap: wrap; }
  .chat-upload-link { font-weight: 700; color: var(--primary); text-decoration: underline; cursor: pointer; background: none; border: none; padding: 0; font-size: inherit; }
  .ent-panel { background: var(--surface); border: 1.5px solid var(--border); border-radius: 11px; padding: 14px 16px; }
  .ent-answer { border-color: rgba(255,91,46,0.28); background: linear-gradient(180deg, var(--card) 0%, var(--surface) 100%); }
  .ent-answer-body { font-size: 14px; line-height: 1.65; color: var(--text); }
  .ent-ref-list { display: flex; flex-direction: column; gap: 10px; }
  .ent-ref-card { padding: 12px; border-radius: 9px; border: 1px solid var(--border); background: var(--bg); }
  .ent-ref-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 16px; margin-top: 8px; }
  @media (max-width: 560px) { .ent-ref-grid { grid-template-columns: 1fr; } }
  .ent-confidence-row { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
  .ent-confidence-pct { font-size: 22px; font-weight: 800; color: var(--primary); }
  .ent-related-chips { display: flex; flex-wrap: wrap; gap: 8px; }
  .ent-doc-chip {
    display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px;
    border-radius: 99px; border: 1.5px solid rgba(255,91,46,0.28); background: var(--primaryL);
    font-size: 12px; font-weight: 600; color: var(--primary); cursor: pointer; transition: all 0.15s;
  }
  .ent-doc-chip:hover { background: var(--primaryL); transform: translateY(-1px); }
  .ent-doc-type { font-size: 10px; font-weight: 800; text-transform: uppercase; opacity: 0.75; }
  .ent-escalation { border-color: #FDE68A; background: linear-gradient(180deg, #FFFBEB 0%, var(--surface) 100%); }
  .ent-feedback {
    display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
    padding-top: 10px; margin-top: 4px; border-top: 1px dashed var(--border);
  }
  .ent-feedback-btn {
    width: 32px; height: 32px; border-radius: 8px; border: 1.5px solid var(--border);
    background: var(--bg); font-size: 14px; transition: all 0.15s;
  }
  .ent-feedback-btn:hover, .ent-feedback-btn.active { border-color: var(--primary); background: var(--primaryL); }
  .ent-feedback-report {
    margin-left: auto; font-size: 11px; font-weight: 700; color: var(--danger);
    background: var(--dangerL); border: 1px solid #FECACA; padding: 5px 10px; border-radius: 7px;
  }
  .ent-feedback-report.reported { color: var(--success); background: var(--successL); border-color: #BBF7D0; }
  .docs-upload-banner {
    margin-bottom: 14px; padding: 14px 16px; border-radius: var(--radius2);
    border: 1.5px solid rgba(255,91,46,0.28); background: linear-gradient(135deg, var(--card), var(--primaryL));
    font-size: 13px; line-height: 1.55; color: var(--text2);
  }
  .docs-upload-banner code { font-family: var(--mono-f); font-size: 11px; background: var(--surface); padding: 2px 6px; border-radius: 4px; }

  /* ── ModelPilot auth gate ── */
  .mp-auth { display:flex; height:100vh; overflow:hidden; background:var(--bg); }
  .mp-left { width:min(480px,42vw); flex-shrink:0; background:var(--ink); color:var(--paper); display:flex; flex-direction:column; justify-content:space-between; padding:44px; position:relative; overflow:hidden; }
  .mp-glow { position:absolute; top:-140px; right:-90px; width:520px; height:520px; background:radial-gradient(circle,rgba(255,91,46,0.16) 0%,transparent 65%); pointer-events:none; }
  .mp-glow2 { position:absolute; bottom:-90px; left:-70px; width:360px; height:360px; background:radial-gradient(circle,rgba(43,76,255,0.10) 0%,transparent 65%); pointer-events:none; }
  .mp-grid { position:absolute; inset:0; background-image:linear-gradient(rgba(255,255,255,0.035) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.035) 1px,transparent 1px); background-size:56px 56px; mask-image:radial-gradient(ellipse 80% 70% at 50% 40%,black 30%,transparent 85%); -webkit-mask-image:radial-gradient(ellipse 80% 70% at 50% 40%,black 30%,transparent 85%); }
  .mp-brand { display:flex; align-items:center; gap:11px; position:relative; z-index:1; }
  .mp-logo { width:38px; height:38px; border-radius:11px; background:conic-gradient(from 210deg,var(--primary),var(--accent-2),var(--accent),var(--gold),var(--primary)); display:grid; place-items:center; position:relative; box-shadow:0 6px 18px rgba(255,91,46,.3); }
  .mp-logo::after { content:""; position:absolute; inset:2px; border-radius:9px; background:var(--ink); }
  .mp-logo span { position:relative; z-index:2; font-family:var(--display); font-style:italic; font-size:20px; color:var(--paper); }
  .mp-brand-name { font-family:var(--display); font-size:22px; letter-spacing:.3px; }
  .mp-brand-name em { font-style:italic; color:var(--primary); }
  .mp-left-main { position:relative; z-index:1; display:flex; flex-direction:column; gap:28px; margin:36px 0; flex:1; }
  .mp-left-h { font-family:var(--display); font-size:clamp(28px,3.2vw,40px); line-height:1.05; font-weight:400; letter-spacing:-.01em; }
  .mp-left-h em { color:var(--primary); font-style:italic; }
  .mp-steps { display:flex; flex-direction:column; gap:16px; }
  .mp-step { display:flex; gap:14px; align-items:flex-start; }
  .mp-step-num { width:30px; height:30px; border-radius:9px; background:rgba(255,91,46,0.12); border:1px solid rgba(255,91,46,0.28); display:flex; align-items:center; justify-content:center; font-family:var(--mono-f); font-size:11px; font-weight:600; color:var(--primary); flex-shrink:0; }
  .mp-step-title { font-size:15px; font-weight:500; color:#faf7f0; }
  .mp-step-sub { font-size:13px; color:#6f6a62; margin-top:2px; }
  .mp-pills { display:flex; flex-wrap:wrap; gap:8px; }
  .mp-pill { display:flex; align-items:center; gap:7px; padding:6px 12px; border-radius:999px; background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.1); font-family:var(--mono-f); font-size:11px; letter-spacing:.4px; color:#a9a39a; }
  .mp-dot { width:6px; height:6px; border-radius:50%; background:var(--primary); display:inline-block; }
  .mp-free { background:rgba(15,122,85,0.12); border:1px solid rgba(15,122,85,0.3); border-radius:12px; padding:14px 16px; display:flex; gap:11px; align-items:flex-start; }
  .mp-free > i { width:8px; height:8px; border-radius:50%; background:var(--accent); margin-top:6px; box-shadow:0 0 0 4px rgba(15,122,85,0.18); flex-shrink:0; }
  .mp-free-t { font-size:13.5px; color:#9fe0c0; font-weight:500; }
  .mp-free-s { font-size:11px; color:rgba(159,224,192,0.6); margin-top:3px; font-family:var(--mono-f); }
  .mp-foot { position:relative; z-index:1; font-family:var(--mono-f); font-size:11px; letter-spacing:.5px; text-transform:uppercase; color:#6f6a62; }
  .mp-foot button { color:#a9a39a; font-weight:500; border-bottom:1px solid rgba(255,255,255,0.1); font-family:inherit; font-size:inherit; letter-spacing:inherit; text-transform:inherit; }
  .mp-right { flex:1; display:flex; align-items:flex-start; justify-content:center; overflow-y:auto; padding:56px 28px; background-image:radial-gradient(ellipse 70% 50% at 50% -10%, rgba(255,91,46,.06), transparent 60%); }
  .mp-card { width:100%; max-width:444px; padding-bottom:40px; animation:mpFade .4s ease both; }
  @keyframes mpFade { from{transform:translateY(12px);opacity:0} to{transform:none;opacity:1} }
  .mp-form-h { font-family:var(--display); font-size:36px; font-weight:400; letter-spacing:-.01em; margin-bottom:6px; }
  .mp-form-sub { font-size:14.5px; color:var(--text2); margin-bottom:28px; font-weight:300; }
  .mp-form-sub button { color:var(--primary); font-weight:500; }
  .mp-progress { display:flex; align-items:center; margin-bottom:28px; gap:0; }
  .mp-prog-wrap { display:flex; align-items:center; flex:1; }
  .mp-prog-wrap:first-child { flex:0; }
  .mp-prog-step { display:flex; align-items:center; gap:8px; }
  .mp-prog-dot { width:24px; height:24px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-family:var(--mono-f); font-size:11px; font-weight:600; }
  .mp-prog-dot.done { background:var(--primary); color:#fff; }
  .mp-prog-dot.active { background:var(--ink); color:var(--paper); }
  .mp-prog-dot.inactive { background:var(--bg-2); color:var(--muted); border:1px solid var(--border); }
  .mp-prog-label { font-family:var(--mono-f); font-size:10.5px; letter-spacing:1px; text-transform:uppercase; }
  .mp-prog-label.active { color:var(--text); font-weight:500; }
  .mp-prog-label.inactive { color:var(--muted); }
  .mp-prog-line { flex:1; height:1px; background:var(--border2); margin:0 10px; min-width:24px; }
  .mp-prog-line.done { background:var(--primary); }
  .mp-field { display:block; margin-bottom:16px; }
  .mp-field span { display:block; font-family:var(--mono-f); font-size:10.5px; font-weight:500; color:var(--text2); margin-bottom:7px; text-transform:uppercase; letter-spacing:1px; }
  .mp-field input { width:100%; padding:12px 14px; border-radius:11px; border:1px solid var(--border2); font-size:14.5px; color:var(--text); background:var(--card); transition:border-color .15s, box-shadow .15s; }
  .mp-field input:focus { border-color:var(--primary); box-shadow:0 0 0 3px rgba(255,91,46,0.12); }
  .mp-row2 { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
  .mp-pw { display:flex; gap:5px; margin-top:8px; }
  .mp-pw i { flex:1; height:3px; border-radius:2px; background:var(--border2); }
  .mp-pw i.on { background:var(--primary); }
  .mp-submit { width:100%; padding:14px; border-radius:999px; border:1px solid var(--ink); background:var(--ink); color:var(--paper); font-size:14.5px; font-weight:500; display:flex; align-items:center; justify-content:center; gap:8px; transition:all .18s; margin-top:8px; }
  .mp-submit:hover:not(:disabled) { background:var(--primary); border-color:var(--primary); color:#fff; }
  .mp-submit:disabled { opacity:.55; cursor:not-allowed; }
  .mp-back { width:100%; padding:12px; border-radius:999px; border:1px solid var(--border2); color:var(--text2); margin-top:10px; transition:all .15s; }
  .mp-back:hover { border-color:var(--ink); color:var(--ink); }
  .mp-demo { width:100%; margin-top:14px; padding:10px; font-size:13px; color:var(--text2); border-radius:999px; border:1px dashed var(--border2); }
  .mp-demo:hover { border-color:var(--primary); color:var(--primary); background:var(--primaryL); }
  .mp-terms { font-family:var(--mono-f); font-size:10.5px; color:var(--muted); text-align:center; margin-top:16px; line-height:1.7; letter-spacing:.3px; }
  .mp-industries { display:grid; grid-template-columns:1fr 1fr; gap:9px; margin-bottom:16px; }
  .mp-ind { padding:13px 14px; border-radius:11px; border:1px solid var(--border2); background:var(--card); text-align:left; font-size:14px; color:var(--text2); transition:all .15s; }
  .mp-ind:hover, .mp-ind.selected { border-color:var(--primary); background:var(--primaryL); color:var(--primary); font-weight:500; }
  .mp-success { text-align:center; padding:24px 0; }
  .mp-success-icon { width:78px; height:78px; border-radius:50%; background:var(--primaryL); border:1px solid rgba(255,91,46,0.3); display:flex; align-items:center; justify-content:center; margin:0 auto 26px; }
  .mp-success-icon svg { width:34px; height:34px; stroke:var(--primary); fill:none; stroke-width:2; }
  .mp-success h3 { font-family:var(--display); font-size:30px; font-weight:400; margin-bottom:12px; }
  .mp-success p { color:var(--text2); }
  .mp-err { color:var(--danger); font-size:12px; margin:10px 0; }
  .mp-spin { width:17px; height:17px; border:2px solid rgba(250,247,240,0.35); border-top-color:var(--paper); border-radius:50%; animation:mpSpin .8s linear infinite; }
  @keyframes mpSpin { to { transform:rotate(360deg); } }
  .mp-slide { animation:mpSlide .32s ease both; }
  @keyframes mpSlide { from{transform:translateX(18px);opacity:0} to{transform:none;opacity:1} }
  @media (max-width:768px) { .mp-left { display:none; } .mp-right { padding:32px 18px; } }
  .mp-back-link { font-family:var(--mono-f); font-size:11px; letter-spacing:.8px; text-transform:uppercase; color:var(--text2); margin-bottom:18px; display:inline-flex; }
  .mp-back-link:hover { color:var(--primary); }
  .mp-left-desc { font-size:14px; color:#6f6a62; line-height:1.7; font-weight:300; max-width:360px; }
  .mp-mini { background:rgba(255,255,255,0.03); border-radius:14px; border:1px solid rgba(255,255,255,0.1); overflow:hidden; margin-top:8px; }
  .mp-mini-bar { background:rgba(0,0,0,0.25); padding:10px 14px; display:flex; gap:6px; border-bottom:1px solid rgba(255,255,255,0.1); }
  .mp-mini-bar i { width:8px; height:8px; border-radius:50%; display:block; }
  .mp-mini-body { padding:16px; }
  .mp-mini-kpis { display:flex; gap:8px; }
  .mp-mini-kpi { flex:1; background:rgba(255,255,255,0.04); border-radius:8px; padding:10px 12px; border:1px solid rgba(255,255,255,0.1); }
  .mp-mini-l { font-family:var(--mono-f); font-size:9px; color:#6f6a62; text-transform:uppercase; letter-spacing:.5px; margin-bottom:3px; }
  .mp-mini-v { font-size:18px; color:#faf7f0; letter-spacing:-0.6px; }
  .mp-mini-t { font-family:var(--mono-f); font-size:9px; color:var(--accent); margin-top:2px; }

  /* ── Landing page (ModelPilot innovative colour) ── */
  .lp { min-height:100vh; background:var(--bg); color:var(--text); position:relative; overflow-x:hidden;
    background-image:radial-gradient(ellipse 80% 50% at 50% -10%, rgba(255,91,46,.12), transparent 60%),
      radial-gradient(ellipse 60% 40% at 92% 18%, rgba(43,76,255,.08), transparent 55%),
      radial-gradient(ellipse 70% 50% at 8% 85%, rgba(15,122,85,.08), transparent 60%),
      radial-gradient(ellipse 50% 40% at 70% 70%, rgba(124,58,237,.05), transparent 55%);
    background-attachment:fixed; }
  .lp ::selection { background:var(--primary); color:#fff; }
  .lp-grain { position:fixed; inset:0; pointer-events:none; z-index:100; opacity:.28; mix-blend-mode:multiply;
    background-image:url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence baseFrequency='0.8' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.6 0 0 0 0 0.55 0 0 0 0 0.45 0 0 0 0.09 0'/></filter><rect width='240' height='240' filter='url(%23n)'/></svg>"); }
  .lp-grid { position:fixed; inset:0; pointer-events:none; z-index:0;
    background-image:linear-gradient(rgba(14,17,22,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(14,17,22,0.07) 1px, transparent 1px);
    background-size:80px 80px; mask-image:radial-gradient(ellipse 80% 60% at 50% 40%, black 30%, transparent 80%); }
  .lp-nav { position:fixed; top:0; left:0; right:0; z-index:50; display:flex; align-items:center; justify-content:space-between; gap:16px; padding:20px 40px;
    backdrop-filter:blur(18px); background:rgba(250,247,240,.86); border-bottom:1px solid var(--border); }
  .lp-logo { display:flex; align-items:center; gap:12px; background:none; border:none; cursor:pointer; padding:0; }
  .lp-mark { width:38px; height:38px; border-radius:11px; background:conic-gradient(from 210deg, var(--primary), var(--accent-2), var(--accent), var(--plum), var(--gold), var(--primary)); display:grid; place-items:center; position:relative; box-shadow:0 8px 22px rgba(198,97,63,.28); isolation:isolate; }
  .lp-mark::after { content:""; position:absolute; inset:2px; border-radius:9px; background:var(--paper); z-index:0; }
  .lp-mark span { position:relative; z-index:2; font-family:var(--display); font-style:italic; font-size:19px; color:var(--ink); }
  .lp-name { font-family:var(--display); font-size:24px; letter-spacing:.3px; color:var(--text); }
  .lp-name em { font-style:italic; color:var(--primary); }
  .lp-links { display:flex; gap:32px; font-family:var(--mono-f); font-size:11px; text-transform:uppercase; letter-spacing:1.5px; }
  .lp-links a { color:var(--text2); position:relative; transition:color .25s; }
  .lp-links a::after { content:""; position:absolute; left:0; right:0; bottom:-6px; height:1px; background:var(--primary); transform:scaleX(0); transform-origin:left; transition:transform .25s; }
  .lp-links a:hover { color:var(--text); }
  .lp-links a:hover::after { transform:scaleX(1); }
  .lp-nav-actions { display:flex; align-items:center; gap:10px; }
  .lp-cta { font-family:var(--mono-f); font-size:11px; letter-spacing:1.5px; text-transform:uppercase; padding:10px 20px; border:1px solid var(--ink); border-radius:999px; background:var(--ink); color:var(--paper); display:inline-flex; align-items:center; gap:8px; transition:all .3s; }
  .lp-cta:hover { background:var(--primary); border-color:var(--primary); color:#fff; box-shadow:0 8px 24px rgba(255,91,46,.25); }
  .lp-ghost { font-family:var(--mono-f); font-size:11px; letter-spacing:1.5px; text-transform:uppercase; padding:10px 18px; border:1px solid var(--border2); border-radius:999px; background:transparent; color:var(--text); transition:all .25s; }
  .lp-ghost:hover { border-color:var(--ink); background:var(--ink); color:var(--paper); }
  .lp-ghost-lg { padding:16px 24px; font-size:12px; }
  .lp-hero { position:relative; z-index:1; min-height:auto; padding:100px 40px 56px; max-width:1200px; margin:0 auto; display:grid; grid-template-columns:1fr minmax(260px,42%); gap:36px; align-items:center; overflow:hidden; }
  .lp-hero-copy { max-width:560px; }
  .lp-hero-visual { position:relative; z-index:2; }
  .lp-hero-shot { width:100%; display:block; }
  .lp-highlights { list-style:none; margin:18px 0 22px; padding:0; display:flex; flex-direction:column; gap:8px; }
  .lp-highlights li { font-size:14px; color:var(--text2); padding-left:18px; position:relative; line-height:1.45; }
  .lp-highlights li::before { content:""; position:absolute; left:0; top:9px; width:6px; height:6px; border-radius:50%; background:var(--primary); }
  .lp-cta-row { display:flex; flex-wrap:wrap; gap:12px; align-items:center; }
  .lp-section-title { font-family:var(--display); font-size:clamp(26px,4vw,38px); font-weight:400; letter-spacing:-.02em; margin-bottom:8px; color:var(--text); }
  .lp-section-sub { color:var(--text2); font-size:15px; line-height:1.55; margin-bottom:24px; max-width:640px; font-weight:300; }
  .lp-features, .lp-preview { padding-top:56px; padding-bottom:64px; }
  .lp-tiles { display:grid; grid-template-columns:repeat(auto-fit,minmax(200px,1fr)); gap:14px; }
  .lp-tile { text-align:left; padding:18px 16px; border:1px solid var(--border); border-radius:16px; background:var(--surface); cursor:pointer; transition:transform .25s, border-color .25s, box-shadow .25s; display:flex; flex-direction:column; gap:6px; box-shadow:0 2px 8px rgba(28,28,28,.04); }
  .lp-tile:hover { border-color:rgba(198,97,63,.35); transform:translateY(-2px); box-shadow:0 12px 28px rgba(198,97,63,.1); }
  .lp-tile-icon { font-size:22px; line-height:1; }
  .lp-tile-title { font-family:var(--display); font-size:17px; color:var(--text); }
  .lp-tile-desc { font-size:13px; color:var(--text2); line-height:1.45; flex:1; }
  .lp-tile-go { font-family:var(--mono-f); font-size:10px; letter-spacing:1px; text-transform:uppercase; color:var(--primary); margin-top:6px; }
  .lp-code { font-family:var(--mono-f); font-size:12px; background:var(--bg-2); padding:2px 8px; border-radius:6px; border:1px solid var(--border); }
  .lp-foot-brand { font-family:var(--display); font-size:16px; }
  .lp-foot-brand em { font-style:italic; color:var(--primary); }
  .lp-foot-meta { font-size:13px; color:var(--text2); }
  .lp-tag { font-family:var(--mono-f); font-size:11px; letter-spacing:3px; text-transform:uppercase; color:var(--primary); margin-bottom:32px; display:flex; align-items:center; gap:14px; animation:lpFadeUp .9s .15s both; }
  .lp-tag::before { content:""; width:40px; height:1px; background:var(--primary); }
  .lp-hero h1 { font-family:var(--display); font-size:clamp(48px, 9vw, 120px); line-height:.92; letter-spacing:-.03em; font-weight:400; margin-bottom:40px; }
  .lp-line { display:block; overflow:visible; }
  .lp-line span { display:inline-block; animation:lpReveal 1.1s cubic-bezier(.2,.9,.2,1) both; }
  .lp-line:nth-child(1) span { animation-delay:.3s; }
  .lp-line:nth-child(2) span { animation-delay:.45s; }
  .lp-line:nth-child(3) span { animation-delay:.6s; }
  @keyframes lpReveal { from { opacity:0; transform:translateY(.35em); } to { opacity:1; transform:none; } }
  @keyframes lpFadeUp { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:none; } }
  .lp-hero h1 em { font-style:italic; color:var(--primary); }
  .lp-hero h1 em.blue { color:var(--accent-2); }
  .lp-hero-meta { display:grid; grid-template-columns:1.35fr 1fr; gap:48px; align-items:end; animation:lpFadeUp 1s .85s both; }
  .lp-desc { font-size:19px; line-height:1.58; color:var(--text2); max-width:540px; font-weight:300; }
  .lp-desc strong { color:var(--text); font-weight:500; }
  .lp-cta-col { display:flex; flex-direction:column; gap:14px; align-items:flex-start; }
  .lp-cta-primary { display:inline-flex; align-items:center; gap:14px; padding:18px 32px; background:var(--ink); color:var(--paper); font-family:var(--mono-f); font-size:12px; letter-spacing:1.5px; text-transform:uppercase; border:none; border-radius:999px; transition:all .35s; position:relative; overflow:hidden; }
  .lp-cta-primary::before { content:""; position:absolute; inset:0; background:linear-gradient(135deg, var(--primary), #c2410c); transform:translateX(-101%); transition:transform .35s; }
  .lp-cta-primary span { position:relative; z-index:2; }
  .lp-cta-primary:hover::before { transform:translateX(0); }
  .lp-cta-primary:hover { color:#fff; box-shadow:0 12px 32px rgba(255,91,46,.3); }
  .lp-arr { width:22px; height:22px; border-radius:50%; background:var(--paper); color:var(--ink); display:grid; place-items:center; position:relative; z-index:2; transition:background .35s; }
  .lp-cta-primary:hover .lp-arr { background:#fff; }
  .lp-cta-group { display:flex; flex-wrap:wrap; gap:12px; align-items:center; }
  .lp-stats { display:flex; flex-wrap:wrap; gap:10px; align-items:center; font-family:var(--mono-f); font-size:10px; letter-spacing:1px; text-transform:uppercase; color:var(--muted); margin-top:8px; }
  .lp-stats strong { color:var(--text); font-weight:600; }
  .lp-stat-sep { opacity:.4; }
  .lp-orbit { position:absolute; right:-120px; top:50%; transform:translateY(-50%); width:680px; height:680px; pointer-events:none; opacity:.7; z-index:0; }
  .lp-orbit svg { width:100%; height:100%; animation:lpSpin 120s linear infinite; }
  .lp-orbit-core { position:absolute; top:50%; left:50%; transform:translate(-50%,-50%); width:160px; height:160px; border-radius:50%;
    background:radial-gradient(circle at 30% 30%, #fff, var(--bg-2) 70%); box-shadow:inset 0 0 40px rgba(255,91,46,.25), 0 20px 80px rgba(43,76,255,.18); animation:lpPulse 4s ease-in-out infinite; }
  .lp-orbit-glyph { position:absolute; top:50%; left:50%; transform:translate(-50%,-50%); font-family:var(--display); font-size:52px; font-style:italic; color:var(--ink); z-index:2; text-align:center; line-height:1; }
  .lp-orbit-glyph em { color:var(--primary); font-style:italic; }
  .lp-orbit-chip { position:absolute; display:inline-flex; align-items:center; gap:6px; padding:8px 14px; background:rgba(250,247,240,.92); backdrop-filter:blur(8px); border:1px solid var(--border2); border-radius:999px; font-family:var(--mono-f); font-size:10px; letter-spacing:1px; text-transform:uppercase; color:var(--text2); box-shadow:0 8px 20px rgba(14,17,22,.08); animation:lpFloat 5s ease-in-out infinite; }
  .lp-oc-dot { width:6px; height:6px; border-radius:50%; display:inline-block; }
  .lp-orbit-chip.orange .lp-oc-dot { background:var(--primary); }
  .lp-orbit-chip.blue .lp-oc-dot { background:var(--accent-2); }
  .lp-orbit-chip.green .lp-oc-dot { background:var(--accent); }
  .lp-orbit-chip.gold .lp-oc-dot { background:var(--gold); }
  .lp-orbit-chip.plum .lp-oc-dot { background:var(--plum); }
  .lp-oc1 { top:8%; left:6%; }
  .lp-oc2 { top:16%; right:6%; animation-delay:1s; }
  .lp-oc3 { bottom:20%; left:2%; animation-delay:2s; }
  .lp-oc4 { bottom:8%; right:12%; animation-delay:1.5s; }
  .lp-oc5 { top:46%; right:-2%; animation-delay:.5s; }
  .lp-orbit-svg { width:100%; height:100%; animation:lpSpin 120s linear infinite; }
  .lp-hero-mock { position:absolute; right:max(0px, calc((100% - 1400px)/2 + 20px)); top:50%; transform:translateY(-42%) rotate(-1deg); width:min(480px, 40vw); z-index:2; animation:lpMockFloat 6s ease-in-out infinite; }
  @keyframes lpMockFloat { 0%,100% { transform:translateY(-42%) rotate(-1deg); } 50% { transform:translateY(-44%) rotate(0deg); } }
  .lp-shot-frame { border-radius:16px; overflow:hidden; box-shadow:0 40px 100px -30px rgba(14,17,22,.35), 0 0 0 1px rgba(14,17,22,.08); background:#1a1a22; }
  .lp-shot-frame.compact { box-shadow:0 16px 40px -20px rgba(14,17,22,.25), 0 0 0 1px rgba(14,17,22,.08); }
  .lp-shot-chrome { display:flex; align-items:center; gap:8px; padding:10px 14px; background:#1a1a22; border-bottom:1px solid rgba(255,255,255,.06); }
  .lp-mock-dot { width:10px; height:10px; border-radius:50%; }
  .lp-mock-dot.red { background:#ff5f57; }
  .lp-mock-dot.amber { background:#febc2e; }
  .lp-mock-dot.green { background:#28c840; }
  .lp-mock-url { margin-left:auto; font-family:var(--mono-f); font-size:10px; color:rgba(255,255,255,.35); letter-spacing:.5px; }
  .lp-shot-img { width:100%; height:auto; display:block; vertical-align:top; }
  .lp-shot-frame.compact .lp-shot-img { max-height:148px; object-fit:cover; object-position:top left; }
  .lp-bento-shot { margin:14px 0 10px; position:relative; z-index:1; }
  .lp-showcase-shot { animation:lpFadeUp .45s ease both; border-radius:18px; overflow:hidden; }
  .lp-gallery-section { padding-top:80px; }
  .lp-gallery { display:grid; grid-template-columns:repeat(2,1fr); gap:20px; }
  .lp-gallery-item { margin:0; }
  .lp-gallery-cap { font-family:var(--mono-f); font-size:10px; letter-spacing:1.2px; text-transform:uppercase; color:var(--muted); margin-top:10px; text-align:center; }
  @media (max-width:900px) { .lp-gallery { grid-template-columns:1fr; } }
  .lp-cap-icon { width:56px; height:56px; margin-bottom:16px; }
  .lp-cap-icon svg { width:100%; height:100%; display:block; border-radius:16px; box-shadow:0 8px 20px rgba(14,17,22,.12); }
  .lp-b-visual { position:relative; z-index:1; margin:12px 0 8px; min-height:72px; }
  .lp-bv-chat .lp-bv-window { margin-left:auto; width:min(200px, 100%); background:#0a0e14; border-radius:12px; overflow:hidden; border:1px solid rgba(255,255,255,.08); box-shadow:0 12px 32px rgba(14,17,22,.2); }
  .lp-bv-bar { height:8px; background:linear-gradient(90deg,#ff5b2e,#2b4cff,#0f7a55); opacity:.85; }
  .lp-bv-chat { padding:10px; display:flex; flex-direction:column; gap:6px; }
  .lp-bv-bubble { font-size:9px; padding:6px 8px; border-radius:8px; max-width:90%; }
  .lp-bv-bubble.u { align-self:flex-end; background:var(--primary); color:#fff; border-radius:8px 8px 2px 8px; }
  .lp-bv-bubble.b { background:rgba(255,255,255,.08); color:#eef1f6; }
  .lp-bv-logs { display:flex; align-items:flex-end; gap:5px; height:64px; padding:0 4px; }
  .lp-bv-logs span { flex:1; border-radius:4px 4px 0 0; background:linear-gradient(180deg,var(--accent-2),var(--primary)); opacity:.85; animation:lpBarGrow .8s ease-out both; transform-origin:bottom; }
  @keyframes lpBarGrow { from { transform:scaleY(0); } to { transform:scaleY(1); } }
  .lp-bv-kb { display:flex; gap:8px; justify-content:flex-end; padding-right:8px; }
  .lp-bv-doc { width:44px; height:56px; background:var(--surface); border:1px solid var(--border2); border-radius:8px; display:grid; place-items:center; font-size:20px; box-shadow:0 6px 16px rgba(14,17,22,.08); }
  .lp-bv-int { display:grid; grid-template-columns:1fr 1fr; gap:6px; width:100px; margin-left:auto; }
  .lp-bv-int-tile { font-family:var(--mono-f); font-size:9px; font-weight:700; padding:10px 6px; text-align:center; border-radius:10px; color:#fff; }
  .lp-bv-int-tile.c0 { background:linear-gradient(145deg,#0f7a55,#064e3b); }
  .lp-bv-int-tile.c1 { background:linear-gradient(145deg,#632ca6,#4c1d95); }
  .lp-bv-int-tile.c2 { background:linear-gradient(145deg,#2b4cff,#1e3a8a); }
  .lp-bv-int-tile.c3 { background:linear-gradient(145deg,#ff5b2e,#c2410c); }
  .lp-bv-itsm { display:flex; justify-content:flex-end; }
  .lp-bv-ticket { display:flex; align-items:center; gap:8px; padding:10px 14px; background:var(--dangerL); border:1px solid #fecaca; border-radius:10px; font-family:var(--mono-f); font-size:11px; font-weight:600; }
  .lp-bv-sev { background:var(--danger); color:#fff; padding:2px 6px; border-radius:4px; font-size:9px; }
  .lp-providers { position:relative; z-index:1; padding:80px 40px; max-width:1400px; margin:0 auto; text-align:center; }
  .lp-providers .lp-num { justify-content:center; }
  .lp-prov-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:16px; margin-top:36px; }
  .lp-prov-card { padding:28px 20px; background:var(--surface); border:1px solid var(--border); border-radius:20px; transition:all .35s; text-align:left; }
  .lp-prov-card:hover { transform:translateY(-4px); box-shadow:0 20px 40px -16px rgba(14,17,22,.15); border-color:var(--border2); }
  .lp-prov-icon { width:52px; height:52px; border-radius:14px; display:grid; place-items:center; font-family:var(--display); font-size:24px; font-style:italic; color:#fff; margin-bottom:14px; box-shadow:0 8px 20px rgba(14,17,22,.15); }
  .lp-prov-card.orange .lp-prov-icon { background:linear-gradient(145deg,var(--primary),#c2410c); }
  .lp-prov-card.blue .lp-prov-icon { background:linear-gradient(145deg,var(--accent-2),#1e3a8a); }
  .lp-prov-card.green .lp-prov-icon { background:linear-gradient(145deg,var(--accent),#064e3b); }
  .lp-prov-card.plum .lp-prov-icon { background:linear-gradient(145deg,var(--plum),#5b21b6); }
  .lp-prov-name { font-family:var(--display); font-size:22px; margin-bottom:6px; }
  .lp-prov-tag { font-family:var(--mono-f); font-size:9px; letter-spacing:1.2px; text-transform:uppercase; color:var(--muted); }
  .lp-arch-section { padding-top:60px; padding-bottom:80px; }
  .lp-arch { margin-top:32px; padding:28px; background:var(--surface); border:1px solid var(--border); border-radius:22px; box-shadow:var(--shadow); }
  .lp-arch-svg { width:100%; height:auto; display:block; }
  @keyframes lpSpin { to { transform:rotate(360deg); } }
  @keyframes lpPulse { 0%,100% { box-shadow:inset 0 0 40px rgba(255,91,46,.2), 0 20px 80px rgba(43,76,255,.15); } 50% { box-shadow:inset 0 0 60px rgba(255,91,46,.35), 0 30px 100px rgba(43,76,255,.22); } }
  @keyframes lpFloat { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-10px); } }
  .lp-marquee { margin-top:0; padding:22px 0; border-top:1px solid var(--border); border-bottom:1px solid var(--border); overflow:hidden; position:relative; z-index:1; }
  .lp-marquee-track { display:inline-flex; gap:60px; animation:lpMarquee 32s linear infinite; font-family:var(--display); font-size:24px; font-style:italic; color:var(--text2); white-space:nowrap; }
  .lp-marquee-track span::after { content:"✦"; margin-left:60px; color:var(--primary); font-style:normal; font-size:16px; }
  @keyframes lpMarquee { to { transform:translateX(-50%); } }
  .lp-section { position:relative; z-index:1; padding:120px 40px; max-width:1400px; margin:0 auto; }
  .lp-num { font-family:var(--mono-f); font-size:11px; letter-spacing:3px; text-transform:uppercase; color:var(--text2); margin-bottom:32px; display:flex; align-items:center; gap:16px; }
  .lp-num::before { content:""; width:48px; height:1px; background:var(--primary); }
  .lp-section h2 { font-family:var(--display); font-size:clamp(36px,5.5vw,72px); line-height:1.02; font-weight:400; letter-spacing:-.02em; max-width:900px; margin-bottom:36px; }
  .lp-section h2 em { font-style:italic; color:var(--primary); }
  .lp-section h2 em.blue { color:var(--accent-2); }
  .lp-section h2 em.green { color:var(--accent); }
  .lp-manifesto { display:grid; grid-template-columns:repeat(3,1fr); gap:36px; padding-top:32px; border-top:1px solid var(--border); }
  .lp-m-card { padding:28px 0; }
  .lp-m-card .n { font-family:var(--mono-f); font-size:11px; letter-spacing:2px; margin-bottom:18px; }
  .lp-m-card.orange .n { color:var(--primary); }
  .lp-m-card.blue .n { color:var(--accent-2); }
  .lp-m-card.green .n { color:var(--accent); }
  .lp-m-card h3 { font-family:var(--display); font-size:28px; font-weight:400; margin-bottom:12px; }
  .lp-m-card p { color:var(--text2); line-height:1.65; font-size:15px; font-weight:300; }
  .lp-products { max-width:none; padding-left:max(40px, calc((100% - 1400px)/2 + 40px)); padding-right:max(40px, calc((100% - 1400px)/2 + 40px)); background:linear-gradient(180deg, transparent, rgba(43,76,255,.05), transparent); }
  .lp-products-head { display:grid; grid-template-columns:1.2fr 1fr; gap:40px; align-items:end; margin-bottom:48px; max-width:1400px; }
  .lp-products-head p { color:var(--text2); font-size:16px; line-height:1.6; font-weight:300; }
  .lp-bento { display:grid; grid-template-columns:repeat(12,1fr); gap:18px; max-width:1400px; }
  .lp-bento-card { position:relative; border:1px solid var(--border); border-radius:22px; padding:32px; background:var(--surface); overflow:hidden; cursor:pointer; transition:all .4s cubic-bezier(.2,.9,.2,1); min-height:280px; display:flex; flex-direction:column; box-shadow:0 2px 8px rgba(14,17,22,.04); }
  .lp-bento-card:hover { transform:translateY(-4px); border-color:var(--border2); box-shadow:0 24px 50px -20px rgba(14,17,22,.16); }
  .lp-bento-card::after { content:""; position:absolute; top:-30%; right:-25%; width:280px; height:280px; border-radius:50%; pointer-events:none; opacity:.75; transition:opacity .4s; }
  .lp-bento-card:hover::after { opacity:1; }
  .lp-b-orange::after { background:radial-gradient(circle, rgba(255,91,46,.14), transparent 60%); }
  .lp-b-blue::after { background:radial-gradient(circle, rgba(43,76,255,.12), transparent 60%); }
  .lp-b-green::after { background:radial-gradient(circle, rgba(15,122,85,.12), transparent 60%); }
  .lp-b-plum::after { background:radial-gradient(circle, rgba(124,58,237,.12), transparent 60%); }
  .lp-b-gold::after { background:radial-gradient(circle, rgba(138,109,46,.12), transparent 60%); }
  .lp-b-badge { position:absolute; top:20px; right:20px; font-family:var(--mono-f); font-size:9px; letter-spacing:1.5px; text-transform:uppercase; padding:5px 10px; border-radius:999px; background:rgba(255,91,46,.08); color:var(--primary); border:1px solid rgba(255,91,46,.22); z-index:1; }
  .lp-b-blue .lp-b-badge { background:rgba(43,76,255,.08); color:var(--accent-2); border-color:rgba(43,76,255,.22); }
  .lp-b-num { font-family:var(--mono-f); font-size:11px; color:var(--muted); letter-spacing:2px; margin-bottom:14px; position:relative; z-index:1; }
  .lp-bento-card h3 { font-family:var(--display); font-size:clamp(28px,3vw,38px); line-height:1; font-weight:400; margin-bottom:10px; position:relative; z-index:1; }
  .lp-bento-card h3 em { font-style:italic; color:var(--primary); }
  .lp-b-blue h3 em { color:var(--accent-2); }
  .lp-b-green h3 em { color:var(--accent); }
  .lp-b-plum h3 em { color:var(--plum); }
  .lp-b-gold h3 em { color:var(--gold); }
  .lp-b-desc { color:var(--text2); font-size:14.5px; line-height:1.6; font-weight:300; max-width:420px; flex:1; position:relative; z-index:1; }
  .lp-b-meta { display:flex; justify-content:space-between; align-items:center; margin-top:20px; padding-top:16px; border-top:1px solid var(--border); font-family:var(--mono-f); font-size:10px; letter-spacing:1px; text-transform:uppercase; color:var(--muted); position:relative; z-index:1; }
  .lp-b-arrow { width:32px; height:32px; border-radius:50%; border:1px solid var(--border2); display:grid; place-items:center; transition:all .35s; }
  .lp-bento-card:hover .lp-b-arrow { background:var(--primary); border-color:var(--primary); color:#fff; transform:rotate(-45deg); }
  .lp-b-hero { grid-column:span 7; min-height:360px; }
  .lp-b-med { grid-column:span 5; }
  .lp-b-third { grid-column:span 4; min-height:240px; }
  .lp-b-half { grid-column:span 8; }
  .lp-model-chips { display:flex; flex-direction:column; gap:8px; align-items:flex-end; margin:16px 0; position:relative; z-index:1; }
  .lp-model-chip { display:inline-flex; align-items:center; gap:7px; padding:7px 12px; background:rgba(250,247,240,.85); backdrop-filter:blur(8px); border:1px solid var(--border2); border-radius:999px; font-family:var(--mono-f); font-size:10px; color:var(--text2); animation:lpFloat 6s ease-in-out infinite; }
  .lp-model-chip i { width:6px; height:6px; border-radius:50%; background:var(--primary); display:inline-block; }
  .lp-model-chip:nth-child(2) i { background:var(--accent-2); animation-delay:1s; }
  .lp-model-chip:nth-child(3) i { background:var(--accent); }
  .lp-model-chip:nth-child(4) i { background:var(--plum); }
  .lp-agent-pills { display:flex; flex-wrap:wrap; gap:7px; margin:12px 0; position:relative; z-index:1; }
  .lp-agent-pill { padding:5px 11px; background:rgba(255,91,46,.06); border:1px solid rgba(255,91,46,.2); border-radius:999px; font-family:var(--mono-f); font-size:9px; color:var(--primary); letter-spacing:1px; text-transform:uppercase; }
  .lp-showcase { position:relative; z-index:1; padding:120px 40px; background:linear-gradient(180deg, var(--surface), var(--bg)); border-top:1px solid var(--border); border-bottom:1px solid var(--border); }
  .lp-showcase-inner { max-width:1400px; margin:0 auto; }
  .lp-showcase-head { display:grid; grid-template-columns:1fr 1fr; gap:40px; align-items:end; margin-bottom:36px; }
  .lp-showcase-head p { color:var(--text2); font-size:16px; line-height:1.6; font-weight:300; }
  .lp-tabs { display:flex; gap:8px; margin-bottom:28px; border-bottom:1px solid var(--border); flex-wrap:wrap; }
  .lp-tab { padding:12px 22px; border:none; background:transparent; font-family:var(--mono-f); font-size:11px; letter-spacing:2px; text-transform:uppercase; color:var(--muted); border-bottom:2px solid transparent; margin-bottom:-1px; transition:color .25s; cursor:pointer; }
  .lp-tab.active { color:var(--text); border-bottom-color:var(--primary); }
  .lp-tab:hover { color:var(--text); }
  .lp-stage { display:none; border-radius:28px; overflow:hidden; padding:36px; min-height:380px; color:var(--paper);
    background:radial-gradient(ellipse 80% 60% at 30% 30%, rgba(255,91,46,.14), transparent 60%), radial-gradient(ellipse 60% 80% at 80% 70%, rgba(124,58,237,.12), transparent 60%), linear-gradient(145deg, #1a1a1f, #0a0a0e); }
  .lp-stage.active { display:flex; flex-direction:column; animation:lpFadeUp .5s ease both; }
  .lp-stage-head { display:flex; justify-content:space-between; align-items:flex-start; gap:20px; margin-bottom:24px; }
  .lp-stage-brand { font-family:var(--display); font-size:36px; line-height:1; }
  .lp-stage-brand em { font-style:italic; color:#ffb185; }
  .lp-stage-tag { font-family:var(--mono-f); font-size:10px; letter-spacing:2px; text-transform:uppercase; color:rgba(255,255,255,.45); text-align:right; }
  .lp-stage-chat { display:flex; flex-direction:column; gap:10px; flex:1; }
  .lp-sc-msg { padding:10px 14px; max-width:88%; border-radius:14px; font-size:13px; line-height:1.5; }
  .lp-sc-msg.bot { background:rgba(255,255,255,.06); color:#eef1f6; border-radius:14px 14px 14px 4px; }
  .lp-sc-msg.user { background:linear-gradient(135deg, var(--primary), #c2410c); color:#fff; align-self:flex-end; border-radius:14px 14px 4px 14px; }
  .lp-sc-panel { padding:12px 14px; background:rgba(255,255,255,.05); border:1px solid rgba(255,255,255,.1); border-radius:12px; font-size:13px; color:#eef1f6; }
  .lp-sc-panel.blue { border-color:rgba(43,76,255,.35); background:rgba(43,76,255,.08); }
  .lp-sc-panel.green { border-color:rgba(15,122,85,.35); background:rgba(15,122,85,.08); }
  .lp-sc-tag { font-family:var(--mono-f); font-size:9px; letter-spacing:1px; text-transform:uppercase; color:#86efac; margin-left:8px; }
  .lp-stage-kpis { display:flex; flex-wrap:wrap; gap:14px; }
  .lp-stage-kpi { min-width:140px; padding:16px 18px; background:rgba(255,255,255,.05); border:1px solid rgba(255,255,255,.1); border-radius:14px; }
  .lp-stage-kpi span { display:block; font-family:var(--mono-f); font-size:9px; letter-spacing:1.5px; text-transform:uppercase; color:rgba(255,255,255,.4); margin-bottom:6px; }
  .lp-stage-kpi strong { font-family:var(--display); font-size:28px; color:#fff; font-weight:400; }
  .lp-stage-kpi.orange strong { color:#ffb185; }
  .lp-stage-kpi.blue strong { color:#93c5fd; }
  .lp-stage-kpi.green strong { color:#86efac; }
  .lp-deploy-sub { color:var(--text2); font-size:15px; }
  .lp-deploy-sub code { font-family:var(--mono-f); font-size:12px; background:var(--bg-2); padding:3px 10px; border-radius:6px; border:1px solid var(--border); }
  .lp-foot { position:relative; z-index:1; border-top:1px solid var(--border); padding:36px 40px; display:flex; flex-wrap:wrap; gap:20px; align-items:center; justify-content:space-between; max-width:1400px; margin:0 auto; }
  .lp-foot-links { display:flex; gap:18px; }
  .lp-foot-links button { font-family:var(--mono-f); font-size:11px; letter-spacing:1px; text-transform:uppercase; color:var(--text2); }
  .lp-foot-links button:hover { color:var(--primary); }
  .lp-foot > p { width:100%; font-family:var(--mono-f); font-size:11px; color:var(--muted); letter-spacing:.4px; }
  @media (max-width:1100px) {
    .lp-b-hero, .lp-b-med, .lp-b-third, .lp-b-half { grid-column:span 12; }
    .lp-orbit, .lp-hero-mock { display:none; }
    .lp-prov-grid { grid-template-columns:repeat(2,1fr); }
  }
  @media (max-width:900px) {
    .lp-links { display:none; }
    .lp-hero { padding:100px 20px 48px; grid-template-columns:1fr; }
    .lp-hero-visual { order:-1; max-width:420px; margin:0 auto; }
    .lp-hero-meta, .lp-manifesto, .lp-products-head, .lp-showcase-head { grid-template-columns:1fr; }
    .lp-section, .lp-nav, .lp-foot, .lp-showcase { padding-left:20px; padding-right:20px; }
    .lp-products { padding-left:20px; padding-right:20px; }
  }

  .btn-primary { background: var(--primary); color: #fff; box-shadow: none; border: 1px solid var(--primary); }
  .btn-primary:hover { background: var(--primary2); border-color: var(--primary2); color: #fff; }
  .send-btn { background: var(--primary); box-shadow: none; }
  .send-btn:hover { background: var(--primary2); }
`;

// ── Data ────────────────────────────────────────────────────────────────────
const SAMPLE_KBS = [
  { id:1, name:"Product Docs",     emoji:"📘", docs:142, queries:2841, color:"var(--primary)", bg:"var(--primaryL)", updated:"2h ago"  },
  { id:2, name:"Support Articles", emoji:"🎯", docs:89,  queries:1203, color:"var(--accent)",  bg:"var(--accentL)",  updated:"4h ago"  },
  { id:3, name:"Engineering Wiki", emoji:"⚙️", docs:234, queries:987,  color:"var(--purple)",  bg:"var(--purpleL)",  updated:"1d ago"  },
  { id:4, name:"HR Policies",      emoji:"📋", docs:56,  queries:412,  color:"var(--warning)", bg:"var(--warningL)", updated:"3d ago"  },
  { id:5, name:"Sales Playbook",   emoji:"💼", docs:78,  queries:1654, color:"var(--success)", bg:"var(--successL)", updated:"6h ago"  },
  { id:6, name:"Legal Contracts",  emoji:"⚖️", docs:31,  queries:189,  color:"var(--amber)",   bg:"var(--amberL)",   updated:"5d ago"  },
];
const SAMPLE_DOCS = [
  { id:1, name:"Q4 Product Roadmap.pdf",    kb:"Product Docs",    size:"2.4 MB", chunks:182, status:"indexed",  added:"Jan 12" },
  { id:2, name:"API Documentation v3.md",   kb:"Engineering Wiki", size:"890 KB", chunks:234, status:"indexed",  added:"Jan 11" },
  { id:3, name:"Onboarding Guide 2024.pdf", kb:"HR Policies",      size:"1.1 MB", chunks:98,  status:"indexed",  added:"Jan 10" },
  { id:4, name:"Sales Deck Q1.pptx",        kb:"Sales Playbook",   size:"5.2 MB", chunks:0,   status:"indexing", added:"Jan 13" },
  { id:5, name:"Support KB Export.csv",     kb:"Support Articles", size:"3.7 MB", chunks:512, status:"indexed",  added:"Jan 09" },
  { id:6, name:"Enterprise Contract v2.pdf",kb:"Legal Contracts",  size:"780 KB", chunks:67,  status:"error",    added:"Jan 08" },
];
const SAMPLE_AGENTS = [
  { id:1, name:"IncidentBot",    desc:"Auto-triage production alerts",         status:"running", runs:2841, lastRun:"2m ago",  emoji:"⚡" },
  { id:2, name:"DocSummarizer",  desc:"Summarise documents on ingestion",       status:"running", runs:1203, lastRun:"8m ago",  emoji:"📝" },
  { id:3, name:"SupportDraft",   desc:"Draft replies from knowledge base",      status:"idle",    runs:987,  lastRun:"1h ago",  emoji:"💬" },
  { id:4, name:"ComplianceBot",  desc:"Weekly check against policy docs",       status:"idle",    runs:54,   lastRun:"2d ago",  emoji:"🛡️" },
  { id:5, name:"DataExtractor",  desc:"Extract structured data from PDFs",      status:"running", runs:432,  lastRun:"now",     emoji:"🔬" },
  { id:6, name:"EmailRouter",    desc:"Route inbound emails to departments",    status:"idle",    runs:2103, lastRun:"30m ago", emoji:"📨" },
];
const SAMPLE_USERS = [
  { id:1, name:"Rajkumar Madhu", email:"rk.madhu2026@gmail.com", role:"Owner",  status:"active",   kbs:6 },
  { id:2, name:"Arun Venkat",    email:"arun@wecrew.in",         role:"Admin",  status:"active",   kbs:4 },
  { id:3, name:"Priya Sundaram", email:"priya@wecrew.in",        role:"Editor", status:"active",   kbs:2 },
  { id:4, name:"Karthik Raja",   email:"karthik@wecrew.in",      role:"Viewer", status:"inactive", kbs:1 },
  { id:5, name:"Divya Krishnan", email:"divya@wecrew.in",        role:"Editor", status:"active",   kbs:3 },
];
const SAMPLE_LOGS = [
  { id:1, action:"API Key Created",       user:"Rajkumar Madhu", resource:"sk-argus-prod-001",  severity:"info"    },
  { id:2, action:"Document Indexed",      user:"System",          resource:"Q4 Roadmap.pdf",     severity:"success" },
  { id:3, action:"Agent Triggered",       user:"IncidentBot",     resource:"ALERT-2024-0142",    severity:"warning" },
  { id:4, action:"User Invited",          user:"Rajkumar Madhu", resource:"divya@wecrew.in",   severity:"info"    },
  { id:5, action:"KB Created",            user:"Arun Venkat",     resource:"Legal Contracts",    severity:"info"    },
  { id:6, action:"Indexing Failed",       user:"System",          resource:"Contract_v2.pdf",    severity:"error"   },
  { id:7, action:"Plan Upgraded",         user:"Rajkumar Madhu", resource:"Pro → Enterprise",   severity:"success" },
  { id:8, action:"API Rate Limit Hit",    user:"System",          resource:"sk-argus-dev-002",   severity:"warning" },
];
const SAMPLE_NOTIFS = [
  { id:1, title:"Indexing complete",  body:"Q4 Product Roadmap.pdf indexed (182 chunks)",  emoji:"✅", time:"2m ago",  read:false, bg:"var(--successL)" },
  { id:2, title:"Agent Alert",        body:"IncidentBot triggered for ALERT-2024-0142",    emoji:"⚡", time:"12m ago", read:false, bg:"var(--warningL)" },
  { id:3, title:"Indexing failed",    body:"Enterprise Contract v2.pdf failed to index",   emoji:"❌", time:"4h ago",  read:false, bg:"var(--dangerL)"  },
  { id:4, title:"New user joined",    body:"Divya Krishnan accepted your invitation",      emoji:"👤", time:"1h ago",  read:true,  bg:"var(--primaryL)" },
  { id:5, title:"Usage milestone",    body:"Your workspace hit 10,000 queries this month", emoji:"🎯", time:"1d ago",  read:true,  bg:"var(--accentL)"  },
  { id:6, title:"API Key expiring",   body:"sk-argus-dev-002 expires in 7 days",           emoji:"🔑", time:"2d ago",  read:true,  bg:"var(--amberL)"   },
];
const SAMPLE_KEYS = [
  { id:1, name:"Production Key", prefix:"sk-argus-prod", created:"Jan 01", lastUsed:"2m ago",  status:"active",   calls:482910 },
  { id:2, name:"Dev / Testing",  prefix:"sk-argus-dev",  created:"Jan 08", lastUsed:"2h ago",  status:"active",   calls:12830  },
  { id:3, name:"Legacy (CI)",    prefix:"sk-argus-ci",   created:"Dec 12", lastUsed:"7d ago",  status:"expiring", calls:89420  },
  { id:4, name:"Mobile App",     prefix:"sk-argus-mob",  created:"Jan 15", lastUsed:"5h ago",  status:"active",   calls:34100  },
];
const NAV_ITEMS = [
  { id:"dashboard", label:"Home",         icon:"📊", section:"main"   },
  { id:"chat",      label:"Ask Copilot",  icon:"💬", section:"main"   },
  { id:"logs",      label:"Logs",         icon:"📋", section:"main"   },
  { id:"kb",        label:"Knowledge",    icon:"🗂️", section:"main"   },
  ...(SHOW_LEARN_UI
    ? [
        { id:"learn-topics", label:"Learn · Topics", icon:"📚", section:"learn" },
        { id:"learn-k8s", label:"Learn · K8s",  icon:"☸️", section:"learn"  },
      ]
    : []),
  { id:"docs",      label:"Documents",    icon:"📄", section:"main"   },
  { id:"incidents", label:"Incidents",    icon:"🎫", section:"itsm"   },
  { id:"changes",   label:"Changes",      icon:"🔄", section:"itsm"   },
  { id:"cmdb",      label:"CMDB",         icon:"🗃️", section:"itsm"   },
  { id:"agents",    label:"Agents",       icon:"🤖", section:"ai"     },
  { id:"architecture", label:"Architecture", icon:"🏗", section:"ai"  },
  { id:"intellirag-it", label:"IT Ops Assistant", icon:"🔧", section:"ai" },
  { id:"ai-tutor", label:"AI Tutor", icon:"🎓", section:"ai" },
  { id:"analytics", label:"Analytics",    icon:"📈", section:"ai"     },
  { id:"users",     label:"Users",        icon:"👥", section:"config" },
  { id:"billing",   label:"Billing",      icon:"💳", section:"config" },
  { id:"apikeys",   label:"API Keys",     icon:"🔑", section:"config" },
  { id:"integrations",label:"Integrations",icon:"🔌",section:"config" },
  { id:"audit",     label:"Audit Logs",   icon:"📋", section:"config" },
  { id:"notifs",    label:"Notifications",icon:"🔔", section:"config" },
  { id:"settings",  label:"Settings",     icon:"⚙️", section:"config" },
];
const BOTTOM_NAV = [
  { id:"dashboard", label:"Home",    icon:"📊" },
  { id:"chat",      label:"Ask",     icon:"💬" },
  { id:"logs",      label:"Logs",    icon:"📋" },
  { id:"kb",        label:"Knowledge", icon:"🗂️" },
  { id:"settings",  label:"Settings",icon:"⚙️" },
];

// ── Toast hook ───────────────────────────────────────────────────────────────
function useToast() {
  const [toasts, setToasts] = useState([]);
  const add = useCallback((msg, type="info") => {
    const id = Date.now();
    setToasts(p => [...p, { id, msg, type }]);
    setTimeout(() => setToasts(p => p.filter(t => t.id !== id)), 3200);
  }, []);
  return { toasts, add };
}

function ScreenLoading() {
  return (
    <div style={{ padding: 32, color: "var(--text2)", fontSize: 14 }}>
      Loading view…
    </div>
  );
}

function Dashboard({ onNavigate }) {
  return (
    <Suspense fallback={<ScreenLoading />}>
      <DashboardView onNavigate={onNavigate} />
    </Suspense>
  );
}

function LogsPage({ domain, onDomainChange, onOpenIncident }) {
  return (
    <PageShell
      title="Observability"
      subtitle="Application · DevOps · Platform · SRE — search, field explorer, event timeline"
      actions={
        <button className="btn btn-outline btn-sm" type="button" disabled title="Requires live log API">
          ⬇ Export
        </button>
      }
    >
      <Suspense fallback={<ScreenLoading />}>
        <LogsExplorer embedded={false} showHeader={false} domain={domain} onDomainChange={onDomainChange} onOpenIncident={onOpenIncident} />
      </Suspense>
    </PageShell>
  );
}

function KnowledgeBases({ toast }) {
  const [kbs, setKbs] = useState([]);
  const [kbId, setKbId] = useState("");
  const [live, setLive] = useState(false);
  const [busy, setBusy] = useState(false);
  const colors = ["var(--primary)", "var(--accent)", "var(--accent-2)", "var(--warning)", "var(--success)", "var(--purple)"];
  const bgs = ["var(--primaryL)", "var(--accentL)", "var(--purpleL)", "var(--warningL)", "var(--successL)", "var(--amberL)"];

  async function load() {
    if (!getToken()) { setKbs(SAMPLE_KBS); setLive(false); return; }
    try {
      const data = await api.listKnowledgeBases();
      const rows = (data || []).map((kb, i) => ({
        id: kb.id, name: kb.name, emoji: kb.emoji || "📘",
        docs: kb.doc_count ?? 0, queries: kb.query_count ?? 0,
        color: colors[i % colors.length], bg: bgs[i % bgs.length],
        updated: kb.updated_at ? new Date(kb.updated_at).toLocaleString() : "—",
      }));
      setKbs(rows);
      if (rows.length) {
        setKbId((prev) => (prev && rows.some((k) => k.id === prev) ? prev : rows[0].id));
      }
      setLive(true);
    } catch (err) {
      setKbs(SAMPLE_KBS); setLive(false);
      toast(`KB API unavailable: ${err.message}`, "info");
    }
  }
  useEffect(() => { load(); }, []);

  async function createKb() {
    if (!getToken()) { toast("Sign in to create a live knowledge base", "info"); return; }
    const name = window.prompt("Knowledge base name", "Support Articles");
    if (!name) return;
    setBusy(true);
    try {
      await api.createKnowledgeBase({ name, description: "", emoji: "📘" });
      toast("Knowledge base created", "success");
      await load();
    } catch (err) { toast(`Create failed: ${err.message}`, "info"); }
    finally { setBusy(false); }
  }

  return (
    <PageShell
      title="Knowledge Bases"
      subtitle={`${kbs.length} bases · ${kbs.reduce((s, k) => s + (k.docs || 0), 0)} documents`}
      live={live}
      actions={
        <div className="flex gap-8" style={{ flexWrap: "wrap", alignItems: "center" }}>
          <KnowledgeUploadBar
            toast={toast}
            page="kb"
            live={live}
            kbs={kbs}
            kbId={kbId}
            onKbIdChange={setKbId}
            onUploaded={load}
          />
          <button className="btn btn-primary" disabled={busy} onClick={createKb}>＋ New Knowledge Base</button>
        </div>
      }
    >
      <div className="docs-upload-banner" style={{ marginBottom: 16 }}>
        <strong style={{ color: "var(--text)" }}>Upload from Knowledge or Documents</strong>
        <div style={{ marginTop: 6 }}>
          Pick a base above and upload {UPLOAD_TYPES_LABEL} — same flow as the Documents page. Demo bases cannot receive uploads.
        </div>
      </div>
      <div className="grid-3">
        {kbs.map((kb) => (
          <div key={kb.id} className="kb-card">
            <div className="flex items-center justify-between">
              <div className="kb-icon" style={{background:kb.bg}}>{kb.emoji}</div>
              <button className="btn btn-ghost btn-icon btn-sm" onClick={()=>toast(`KB ${kb.id}`,"info")}>⋮</button>
            </div>
            <div>
              <div className="fw-700" style={{fontSize:15,marginBottom:3}}>{kb.name}</div>
              <div className="text-sm text-muted">Updated {kb.updated}</div>
            </div>
            <div className="flex gap-8">
              <div style={{flex:1,background:"var(--bg)",borderRadius:9,padding:"8px 10px",textAlign:"center",border:"1.5px solid var(--border)"}}>
                <div className="outfit fw-700" style={{fontSize:18,color:kb.color}}>{kb.docs}</div>
                <div className="text-xs text-muted">Docs</div>
              </div>
              <div style={{flex:1,background:"var(--bg)",borderRadius:9,padding:"8px 10px",textAlign:"center",border:"1.5px solid var(--border)"}}>
                <div className="outfit fw-700" style={{fontSize:18,color:kb.color}}>{Number(kb.queries||0).toLocaleString()}</div>
                <div className="text-xs text-muted">Queries</div>
              </div>
            </div>
          </div>
        ))}
        {kbs.length===0 && (
          <div className="card" style={{gridColumn:"1 / -1",textAlign:"center",padding:40}}>
            <div className="fw-700 mb-8">No knowledge bases yet</div>
            <button className="btn btn-primary" onClick={createKb}>＋ Create first KB</button>
          </div>
        )}
      </div>
    </PageShell>
  );
}

function Documents({ toast }) {
  const [search, setSearch] = useState("");
  const [docs, setDocs] = useState(SAMPLE_DOCS);
  const [kbs, setKbs] = useState([]);
  const [kbId, setKbId] = useState("");
  const [live, setLive] = useState(false);

  async function load() {
    if (!getToken()) { setDocs(SAMPLE_DOCS); setLive(false); return; }
    try {
      const [kbList, docList] = await Promise.all([api.listKnowledgeBases(), api.listDocuments()]);
      setKbs(kbList || []);
      if (kbList?.length && !kbId) setKbId(kbList[0].id);
      const kbNames = Object.fromEntries((kbList || []).map((k) => [k.id, k.name]));
      setDocs((docList || []).map((d) => ({
        id: d.id, name: d.name, kb: kbNames[d.kb_id] || d.kb_id,
        size: d.file_size ? `${Math.max(1, Math.round(d.file_size / 1024))} KB` : "—",
        status: d.status,
      })));
      setLive(true);
    } catch (err) {
      setDocs(SAMPLE_DOCS); setLive(false);
      toast(`Documents API: ${err.message}`, "info");
    }
  }
  useEffect(() => { load(); }, []);
  const filtered = docs.filter((d) => d.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <PageShell
      title="Documents"
      subtitle={`${docs.length} documents · SOPs and runbooks for L2 Chat`}
      live={live}
      actions={
        <div className="flex gap-8" style={{ flexWrap: "wrap", alignItems: "center" }}>
          <KnowledgeUploadBar
            toast={toast}
            page="docs"
            live={live}
            kbs={kbs}
            kbId={kbId}
            onKbIdChange={setKbId}
            onUploaded={load}
            compact
          />
          <div className="input-wrap">
            <span style={{ color: "var(--muted)" }}>🔍</span>
            <input placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
        </div>
      }
    >
      <div className="docs-upload-banner">
        <strong style={{color:"var(--text)"}}>Add knowledge for the L2 Chatbot</strong>
        <div style={{marginTop:6}}>
          Upload approved SOPs and runbooks ({UPLOAD_TYPES_LABEL}) to a Knowledge Base. After indexing, Chat retrieves citations via RAG.
        </div>
      </div>
      <div className="card" style={{padding:0}}>
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>Name</th><th>Knowledge Base</th><th>Size</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {filtered.map(doc=>(
                <tr key={doc.id}>
                  <td><span className="fw-600">📄 {doc.name}</span></td>
                  <td><span className="tag tag-blue">{doc.kb}</span></td>
                  <td className="mono text-sm text-muted">{doc.size}</td>
                  <td><span className={`tag ${doc.status==="indexed"?"tag-green":(doc.status==="indexing"||doc.status==="pending")?"tag-amber":"tag-red"}`}>{doc.status}</span></td>
                  <td><button className="btn btn-ghost btn-icon btn-sm" onClick={()=>toast(doc.name,"info")}>👁</button></td>
                </tr>
              ))}
              {filtered.length===0 && <tr><td colSpan={5} style={{textAlign:"center",padding:28}} className="text-muted">No documents yet — upload an SOP to get started.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </PageShell>
  );
}

function Agents({ toast }) {
  const [agents, setAgents] = useState(SAMPLE_AGENTS);
  function toggle(id) { setAgents(p=>p.map(a=>a.id===id?{...a,status:a.status==="running"?"idle":"running"}:a)); toast("Agent updated","success"); }
  return (
    <div>
      <div className="page-header">
        <div><div className="page-title">AI Agents</div><div className="page-sub">{agents.filter(a=>a.status==="running").length} running · {agents.filter(a=>a.status==="idle").length} idle</div></div>
        <button className="btn btn-primary" onClick={()=>toast("Agent builder — coming soon!","info")}>＋ New Agent</button>
      </div>
      <div className="grid-3">
        {agents.map(ag=>(
          <div key={ag.id} className="agent-card">
            <div className="flex items-center justify-between mb-14">
              <span style={{fontSize:26}}>{ag.emoji}</span>
              <div className="flex items-center gap-8">
                <span className={`tag ${ag.status==="running"?"tag-green":"tag-amber"}`}>● {ag.status}</span>
                <div className={`toggle ${ag.status==="running"?"on":""}`} onClick={()=>toggle(ag.id)}/>
              </div>
            </div>
            <div className="fw-700" style={{fontSize:14,marginBottom:5}}>{ag.name}</div>
            <div className="text-sm text-muted" style={{marginBottom:12,lineHeight:1.5}}>{ag.desc}</div>
            <div className="flex justify-between text-xs text-muted">
              <span>🔄 {ag.runs.toLocaleString()} runs</span>
              <span>🕐 {ag.lastRun}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Analytics() {
  const [data, setData] = useState(null);
  const [live, setLive] = useState(false);
  useEffect(() => {
    if (!getToken()) return;
    api.analytics().then((d) => { setData(d); setLive(true); }).catch(() => setLive(false));
  }, []);
  const total = data?.total_queries ?? 48291;
  const latency = data?.avg_latency_ms != null ? `${(data.avg_latency_ms/1000).toFixed(2)}s` : "1.24s";
  const top = (data?.top_queries?.length ? data.top_queries : [
    {query:"What is the refund policy?",count:1842},
    {query:"How do I reset my password?",count:1230},
    {query:"API rate limits",count:987},
    {query:"Enterprise pricing",count:754},
  ]).slice(0,4);
  const over = data?.queries_over_time?.length ? data.queries_over_time.slice(-6) : null;
  const qData = over ? over.map((x)=>x.count) : [12400,15200,18900,22100,31800,48291];
  const months = over ? over.map((x)=>String(x.date).slice(5)) : ["Aug","Sep","Oct","Nov","Dec","Jan"];
  const maxQ = Math.max(...qData, 1);
  const maxTop = Math.max(...top.map((t)=>t.count||t.count), 1);
  return (
    <PageShell title="Analytics" subtitle="Query volume, latency, and top questions" live={live}>
      <div className="kpi-grid mb-20">
        {[
          {label:"Total Queries", value:Number(total).toLocaleString(), icon:"🔍", cls:"blue"},
          {label:"Avg Latency", value:latency, icon:"⚡", cls:"teal"},
          {label:"Tokens used", value:Number(data?.total_tokens||0).toLocaleString(), icon:"💾", cls:"green"},
          {label:"KBs tracked", value:String(data?.queries_by_kb?.length||0), icon:"🛡️", cls:"amber"},
        ].map(k=>(
          <div key={k.label} className={`kpi ${k.cls}`}>
            <div className="kpi-icon">{k.icon}</div>
            <div className="kpi-label">{k.label}</div>
            <div className="kpi-value">{k.value}</div>
          </div>
        ))}
      </div>
      <div className="grid-2 mb-14">
        <div className="card">
          <div className="card-title mb-4">Query Volume</div>
          <div className="card-sub mb-14">{live ? "From live query logs" : "Demo trend data"}</div>
          <div style={{display:"flex",alignItems:"flex-end",gap:10,height:130}}>
            {qData.map((v,i)=>(
              <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:5}}>
                <span className="mono text-xs text-muted">{v>=1000?`${(v/1000).toFixed(1)}k`:v}</span>
                <div style={{width:"100%",borderRadius:"6px 6px 0 0",height:`${(v/maxQ)*100}px`,background:"linear-gradient(180deg,var(--primary),var(--accent-2))"}}/>
                <span className="text-xs text-muted">{months[i]}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="card">
          <div className="card-title mb-14">Top Queries</div>
          {top.map((q)=>(
            <div key={q.query||q.q} style={{marginBottom:12}}>
              <div className="flex justify-between text-sm mb-4">
                <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:200}}>{q.query||q.q}</span>
                <span className="mono text-muted">{q.count}</span>
              </div>
              <div className="prog-bar"><div className="prog-fill" style={{width:`${Math.round(((q.count||0)/maxTop)*100)}%`,background:"var(--primary)"}}/></div>
            </div>
          ))}
        </div>
      </div>
    </PageShell>
  );
}

function Users({ toast }) {
  return (
    <div>
      <div className="page-header">
        <div><div className="page-title">Users & Roles</div><div className="page-sub">5 members</div></div>
        <button className="btn btn-primary" onClick={()=>toast("Invite sent!","success")}>✉ Invite User</button>
      </div>
      <div className="card" style={{padding:0}}>
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>User</th><th>Role</th><th>Status</th><th>KBs</th><th></th></tr></thead>
            <tbody>
              {SAMPLE_USERS.map(u=>(
                <tr key={u.id}>
                  <td>
                    <div className="flex items-center gap-12">
                      <div className="avatar" style={{width:30,height:30,fontSize:12}}>{u.name[0]}</div>
                      <div><div className="fw-600 text-sm">{u.name}</div><div className="text-xs text-muted">{u.email}</div></div>
                    </div>
                  </td>
                  <td><span className={`tag ${u.role==="Owner"?"tag-purple":u.role==="Admin"?"tag-blue":u.role==="Editor"?"tag-teal":"tag-amber"}`}>{u.role}</span></td>
                  <td><span className={`tag ${u.status==="active"?"tag-green":"tag-amber"}`}>● {u.status}</span></td>
                  <td className="mono text-sm">{u.kbs}/6</td>
                  <td><button className="btn btn-ghost btn-icon btn-sm" onClick={()=>toast(`Editing ${u.name}`,"info")}>✏️</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Billing({ toast }) {
  return (
    <div>
      <div className="page-title mb-20">Billing & Plans</div>
      <div className="grid-3 mb-20">
        {[
          {name:"Starter",  price:"$49",  features:["10K queries/mo","3 KBs","2 Agents","Email support"],                    active:false},
          {name:"Pro",      price:"$149", features:["100K queries/mo","10 KBs","10 Agents","Priority support","Analytics"],  active:true },
          {name:"Enterprise",price:"Custom",features:["Unlimited","Unlimited KBs","Unlimited Agents","SSO","Dedicated CSM"],active:false},
        ].map(plan=>(
          <div key={plan.name} className={`plan-card ${plan.active?"active-plan":""}`}>
            {plan.active&&<div className="tag tag-blue mb-8">Current Plan</div>}
            <div className="outfit fw-700" style={{fontSize:16,marginBottom:8}}>{plan.name}</div>
            <div className="flex items-end gap-4 mb-14">
              <span className="plan-price">{plan.price}</span>
              {plan.price!=="Custom"&&<span className="text-muted" style={{marginBottom:4}}>/mo</span>}
            </div>
            {plan.features.map(f=><div key={f} className="plan-feature"><span style={{color:"var(--accent)"}}>✓</span><span>{f}</span></div>)}
            <button className={`btn ${plan.active?"btn-outline":"btn-primary"} w-full`} style={{marginTop:16,justifyContent:"center"}} onClick={()=>toast(plan.active?"Already on this plan":`Upgrading to ${plan.name}…`,plan.active?"info":"success")}>
              {plan.active?"Current Plan":plan.name==="Enterprise"?"Contact Sales":"Upgrade"}
            </button>
          </div>
        ))}
      </div>
      <div className="card">
        <div className="card-title mb-14">Usage This Month</div>
        {[{l:"Queries",u:48291,t:100000,pct:48,c:"var(--primary)"},{l:"Documents",u:630,t:1000,pct:63,c:"var(--accent)"},{l:"Agents",u:6,t:10,pct:60,c:"var(--purple)"},{l:"API Calls",u:12830,t:50000,pct:26,c:"var(--success)"}].map(u=>(
          <div key={u.l} style={{marginBottom:14}}>
            <div className="flex justify-between text-sm mb-4"><span className="fw-600">{u.l}</span><span className="mono text-muted">{u.u.toLocaleString()} / {u.t.toLocaleString()}</span></div>
            <div className="prog-bar" style={{height:6}}><div className="prog-fill" style={{width:`${u.pct}%`,background:u.c}}/></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function APIKeys({ toast }) {
  const [keys, setKeys] = useState(SAMPLE_KEYS);
  const [live, setLive] = useState(false);
  const [busy, setBusy] = useState(false);

  async function load() {
    if (!getToken()) { setKeys(SAMPLE_KEYS); setLive(false); return; }
    try {
      const data = await api.listApiKeys();
      setKeys((data || []).map((k) => ({
        id: k.id, name: k.name, prefix: k.key_prefix,
        lastUsed: k.last_used_at ? new Date(k.last_used_at).toLocaleString() : "Never",
        calls: k.call_count || 0, status: k.is_active ? "active" : "revoked",
      })));
      setLive(true);
    } catch (err) {
      setKeys(SAMPLE_KEYS); setLive(false);
      toast(`API keys: ${err.message}`, "info");
    }
  }
  useEffect(() => { load(); }, []);

  async function createKey() {
    if (!getToken()) { toast("Sign in to generate a live API key", "info"); return; }
    const name = window.prompt("Key name", "Production");
    if (!name) return;
    setBusy(true);
    try {
      const created = await api.createApiKey(name);
      if (created?.raw_key) {
        try { await navigator.clipboard.writeText(created.raw_key); } catch {}
        toast(`Key created — copied once: ${created.raw_key.slice(0, 24)}…`, "success");
      } else toast("Key created", "success");
      await load();
    } catch (err) { toast(`Create failed: ${err.message}`, "info"); }
    finally { setBusy(false); }
  }

  async function revoke(id) {
    if (!getToken()) { toast("Sign in required", "info"); return; }
    try {
      await api.revokeApiKey(id);
      toast("Key revoked", "success");
      await load();
    } catch (err) { toast(`Revoke failed: ${err.message}`, "info"); }
  }

  return (
    <PageShell
      title="API Keys"
      subtitle={`${keys.filter((k) => k.status === "active").length} active keys`}
      live={live}
      actions={
        <button className="btn btn-primary" disabled={busy} onClick={createKey}>🗝️ Generate Key</button>
      }
    >
      <div className="card" style={{ padding: 0 }}>
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>Name</th><th>Key</th><th>Last Used</th><th>Calls</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {keys.map(k=>(
                <tr key={k.id}>
                  <td className="fw-600">{k.name}</td>
                  <td><span className="mono text-sm text-muted">{k.prefix}-••••••</span></td>
                  <td className="text-muted text-sm">{k.lastUsed}</td>
                  <td className="mono text-sm">{Number(k.calls||0).toLocaleString()}</td>
                  <td><span className={`tag ${k.status==="active"?"tag-green":"tag-amber"}`}>● {k.status}</span></td>
                  <td><div className="flex gap-8"><button className="btn btn-danger btn-icon btn-sm" onClick={()=>revoke(k.id)}>🗑️</button></div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </PageShell>
  );
}

function AuditLogs() {
  const sev = {info:"🔵",success:"🟢",warning:"🟡",error:"🔴"};
  const bg  = {info:"var(--primaryL)",success:"var(--successL)",warning:"var(--warningL)",error:"var(--dangerL)"};
  return (
    <div>
      <div className="page-header">
        <div><div className="page-title">Audit Logs</div><div className="page-sub">SOC2 compliant · Full activity history</div></div>
        <button className="btn btn-outline">⬇ Export CSV</button>
      </div>
      <div className="card">
        {SAMPLE_LOGS.map(log=>(
          <div key={log.id} className="log-row">
            <div style={{width:30,height:30,borderRadius:8,background:bg[log.severity],display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,flexShrink:0}}>{sev[log.severity]}</div>
            <div className="flex-1 min-w-0">
              <div className="fw-600 text-sm">{log.action}</div>
              <div className="text-xs text-muted" style={{marginTop:2}}>{log.resource} · {log.user}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Notifications({ toast }) {
  const [notifs, setNotifs] = useState(SAMPLE_NOTIFS);
  return (
    <div>
      <div className="page-header">
        <div><div className="page-title">Notifications</div><div className="page-sub">{notifs.filter(n=>!n.read).length} unread</div></div>
        <button className="btn btn-outline btn-sm" onClick={()=>{setNotifs(p=>p.map(n=>({...n,read:true})));toast("All marked as read","success");}}>✓ Mark all read</button>
      </div>
      <div className="card">
        {notifs.map(n=>(
          <div key={n.id} className="notif-item" style={{opacity:n.read?0.55:1}}>
            <div style={{width:38,height:38,borderRadius:10,background:n.bg,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>{n.emoji}</div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-8"><span className="fw-600 text-sm">{n.title}</span>{!n.read&&<div className="dot dot-blue" style={{width:6,height:6}}/>}</div>
              <div className="text-xs text-muted" style={{marginTop:3}}>{n.body}</div>
            </div>
            <span className="text-xs text-muted" style={{whiteSpace:"nowrap",flexShrink:0}}>{n.time}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Settings({ toast, onSignOut }) {
  const [apiKey, setApiKey] = useState("");
  const [on1, setOn1] = useState(true);
  const [on2, setOn2] = useState(true);
  const [on3, setOn3] = useState(false);
  return (
    <div>
      <div className="page-title mb-20">Settings</div>
      <div className="grid-2" style={{gap:18}}>
        <div>
          <div className="card mb-14">
            <div className="settings-label">API Configuration</div>
            <div className="text-sm text-muted mb-8">LLM credentials are configured server-side by your workspace admin.</div>
            <div className="flex gap-8">
              <input className="input-full flex-1" type="password" placeholder="Not stored in browser" value={apiKey} onChange={e=>setApiKey(e.target.value)} disabled/>
              <button className="btn btn-primary" onClick={()=>toast("API key saved!","success")}>Save</button>
            </div>
          </div>
          <div className="card">
            <div className="settings-label">RAG Settings</div>
            <div className="settings-row"><div><div className="fw-600 text-sm">Auto-index uploads</div><div className="text-xs text-muted">Index new docs automatically</div></div><div className={`toggle ${on1?"on":""}`} onClick={()=>{setOn1(!on1);toast("Saved","success");}}/></div>
            <div className="settings-row"><div><div className="fw-600 text-sm">Cache responses</div><div className="text-xs text-muted">Speed up repeated queries</div></div><div className={`toggle ${on2?"on":""}`} onClick={()=>{setOn2(!on2);toast("Saved","success");}}/></div>
            <div className="settings-row"><div><div className="fw-600 text-sm">Verbose citations</div><div className="text-xs text-muted">Show full source metadata</div></div><div className={`toggle ${on3?"on":""}`} onClick={()=>{setOn3(!on3);toast("Saved","success");}}/></div>
          </div>
        </div>
        <div>
          <div className="card">
            <div className="settings-label">Account</div>
            <div className="settings-row">
              <div>
                <div className="fw-600 text-sm">Sign out</div>
                <div className="text-xs text-muted">End your session on this device</div>
              </div>
              <button type="button" className="btn btn-outline btn-sm" onClick={onSignOut}>Sign out</button>
            </div>
          </div>
          <div className="card mb-14">
            <div className="settings-label">Workspace</div>
            {[{l:"Workspace Name",v:"Wecrew Copilot"},{l:"Plan",v:"Pro · Enterprise"},{l:"Region",v:"Asia Pacific (Mumbai)"},{l:"Data Retention",v:"90 days"}].map(s=>(
              <div key={s.l} className="settings-row">
                <div><div className="fw-600 text-sm">{s.l}</div><div className="text-xs text-muted">{s.v}</div></div>
                <button className="btn btn-ghost btn-sm" onClick={()=>toast("Saved","success")}>Edit</button>
              </div>
            ))}
          </div>
          <div className="card">
            <div className="settings-label">Danger Zone</div>
            <div className="settings-row">
              <div><div className="fw-600 text-sm">Export all data</div><div className="text-xs text-muted">GDPR-compliant export</div></div>
              <button className="btn btn-outline btn-sm" onClick={()=>toast("Export started","info")}>⬇ Export</button>
            </div>
            <div className="settings-row">
              <div><div className="fw-600 text-sm" style={{color:"var(--danger)"}}>Delete workspace</div><div className="text-xs text-muted">Permanent · cannot undo</div></div>
              <button className="btn btn-danger btn-sm" onClick={()=>toast("Action blocked in demo","info")}>Delete</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── App ───────────────────────────────────────────────────────────────────────
export default function App() {
  useEffect(() => {
    let styleEl = document.getElementById("wecrew-global-styles");
    if (!styleEl) {
      styleEl = document.createElement("style");
      styleEl.id = "wecrew-global-styles";
      document.head.appendChild(styleEl);
    }
    if (styleEl.dataset.version !== GLOBAL_STYLE_VERSION || !styleEl.textContent.includes("#ecece7")) {
      styleEl.textContent = STYLES;
      styleEl.dataset.version = GLOBAL_STYLE_VERSION;
    }
    document.documentElement.setAttribute("data-global-style", GLOBAL_STYLE_VERSION);
  }, []);

  const [route, setRoute] = useState(() => getRoute());
  const [authed, setAuthed] = useState(() =>
    isProductionConsole ? Boolean(getToken()) : hasSession() || getRoute() === "app"
  );
  const [page, setPage] = useState(() => getAppPage());
  const [logsDomain, setLogsDomain] = useState(() => readLogsDomain());
  const [itsmIncidentId, setItsmIncidentId] = useState(null);
  const [chatInitialQuery, setChatInitialQuery] = useState(null);
  const [chatSessionKey, setChatSessionKey] = useState(0);
  const [sideOpen, setSideOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { toasts, add:toast } = useToast();
  const unread = SAMPLE_NOTIFS.filter(n=>!n.read).length;
  const openIncidents = countOpenIncidents();

  useEffect(() => {
    const sync = () => {
      const nextRoute = getRoute();
      setRoute(nextRoute);
      if (nextRoute === "app") setPage(getAppPage());
    };
    const cleanupAuth = installProductionAuth({
      setAuthed,
      toast,
      goRoute,
      setRoute,
      setUserMenuOpen,
      setChatInitialQuery,
      setPage,
      getAppPage,
      getRoute,
    });
    window.addEventListener("hashchange", sync);
    sync();
    // Default first visit → landing (not auto-app)
    if (!window.location.hash || window.location.hash === "#") {
      goRoute("landing");
    }
    // Direct #/app without a live token (clears stale demo session)
    if (getRoute() === "app" && isProductionConsole && !getToken()) {
      removeItem("wecrew_session");
      goRoute("signin");
      setAuthed(false);
    } else if (getRoute() === "app" && !hasSession()) {
      setItem("wecrew_session", "demo");
      setAuthed(true);
    }
    return () => {
      cleanupAuth();
      window.removeEventListener("hashchange", sync);
    };
  }, []);

  function navigatePublic(next) {
    if ((next === "app" || APP_PAGE_IDS.has(next)) && isProductionConsole && !getToken()) {
      goRoute("signin");
      setRoute("signin");
      setAuthed(false);
      return;
    }
    if ((next === "app" || APP_PAGE_IDS.has(next)) && !hasSession()) {
      setItem("wecrew_session", "demo");
      setAuthed(true);
    }
    goRoute(next);
    setRoute(getRoute());
    if (getRoute() === "app") {
      const entry = getItem("wecrew_entry_page", "session");
      if (entry) {
        setPage(entry);
        removeItem("wecrew_entry_page", "session");
      } else {
        setPage(getAppPage());
      }
    }
  }

  function enterApp() {
    setAuthed(true);
    const pending = getItem("wecrew_pending_chat", "session");
    if (pending) {
      removeItem("wecrew_pending_chat", "session");
      setChatInitialQuery(pending);
      goRoute("chat");
      setRoute("app");
      setPage("chat");
      return;
    }
    goRoute("app");
    setRoute("app");
    const entry = getItem("wecrew_entry_page", "session");
    if (entry) {
      setPage(entry);
      removeItem("wecrew_entry_page", "session");
    } else {
      setPage(getAppPage());
    }
  }

  function signOut() {
    clearSession();
    setAuthed(false);
    setUserMenuOpen(false);
    setSideOpen(false);
    goRoute("landing");
    setRoute("landing");
    toast("Signed out", "info");
  }

  if (route === "signup") {
    return <SignUpPage onSuccess={enterApp} onNavigate={navigatePublic} />;
  }
  if (route === "signin") {
    return <SignInPage onSuccess={enterApp} onNavigate={navigatePublic} />;
  }
  if (route !== "app") {
    return (
      <LandingPage
        authed={authed}
        onSignOut={signOut}
        onNavigate={(r) => {
          if ((r === "app" || APP_PAGE_IDS.has(r)) && !authed) {
            if (isProductionConsole) {
              navigatePublic("signin");
              return;
            }
            setItem("wecrew_session", "demo");
            setAuthed(true);
          }
          navigatePublic(r);
        }}
      />
    );
  }

  if (!authed) {
    return <SignInPage onSuccess={enterApp} onNavigate={navigatePublic} />;
  }

  function requireSignIn(pendingQuery, authRoute = "signin") {
    if (getToken()) {
      setAuthed(true);
      if (pendingQuery) {
        setChatInitialQuery(pendingQuery);
        goRoute("chat");
        setPage("chat");
        setRoute("app");
      }
      return;
    }
    if (pendingQuery) setItem("wecrew_pending_chat", pendingQuery, "session");
    goRoute(authRoute);
    setRoute(authRoute);
    setAuthed(false);
  }

  function nav(id, opts = {}) {
    if (opts.domain) {
      setLogsDomain(opts.domain);
      persistLogsDomain(opts.domain);
    }
    if (opts.incidentId) setItsmIncidentId(opts.incidentId);
    else if (id !== "incidents") setItsmIncidentId(null);
    if (opts.chatQuery) setChatInitialQuery(opts.chatQuery);
    setPage(id);
    goRoute(id === "dashboard" ? "app" : id);
    setSideOpen(false);
  }

  function openUpgradeRunbook(prompt) {
    nav("chat", { chatQuery: prompt });
    toast("Opened upgrade runbook in Chat", "info");
  }

  function openLogsDomain(domain) {
    nav("logs", { domain });
  }

  function openIncident(incidentId, opts = {}) {
    nav("incidents", { incidentId, ...opts });
  }

  function openIncidentLogs(domain, query) {
    setLogsDomain(domain);
    persistLogsDomain(domain);
    nav("logs", { domain });
    toast(`Opened Observability · ${domain}`, "info");
  }

  function openK8sChat(prompt) {
    nav("chat", { chatQuery: prompt });
  }

  const screens = {
    dashboard:<Dashboard onNavigate={nav}/>,
    chat:<Chat key={chatSessionKey} onOpenLogs={openLogsDomain} onNavigate={nav} onRequireSignIn={requireSignIn} initialQuery={chatInitialQuery} onInitialQueryConsumed={() => setChatInitialQuery(null)} />,
    logs:<LogsPage domain={logsDomain} onDomainChange={setLogsDomain} onOpenIncident={openIncident}/>,
    incidents:<ItsmIncidents initialIncidentId={itsmIncidentId} onOpenLogs={openIncidentLogs} />,
    changes:<ItsmChanges onOpenIncident={openIncident} onOpenUpgradeRunbook={openUpgradeRunbook} />,
    cmdb:<ItsmCmdb onOpenIncident={openIncident} />,
    kb:<KnowledgeBases toast={toast}/>,
    ...(SHOW_LEARN_UI
      ? {
          "learn-topics": <TopicsLearn onAskChat={openK8sChat} onNavigate={nav} />,
          "learn-k8s": <KubernetesLearn onAskChat={openK8sChat} />,
        }
      : {}),
    docs:<Documents toast={toast}/>,
    agents:<Agents toast={toast}/>,
    architecture:(
      <PageShell title="Platform architecture" subtitle="Product family, MCP connectors, query flows, and repo structure" live={false}>
        <PlatformArchitecture onAskChat={openK8sChat} />
      </PageShell>
    ),
    "intellirag-it": <IntelliRagIT embedded onRequireSignIn={requireSignIn} />,
    "ai-tutor": <IntelliRagTutor embedded onRequireSignIn={requireSignIn} />,
    analytics:<Analytics/>,
    users:<Users toast={toast}/>, billing:<Billing toast={toast}/>, apikeys:<APIKeys toast={toast}/>,
    integrations:<Integrations toast={toast} onNavigate={nav} onAsk={(p) => nav("chat", { chatQuery: p })} />, audit:<AuditLogs/>,
    notifs:<Notifications toast={toast}/>, settings:<Settings toast={toast} onSignOut={signOut}/>
  };

  const sections = {
    main:   ["dashboard","chat","logs","kb","docs"],
    ...(SHOW_LEARN_UI ? { learn: ["learn-topics", "learn-k8s"] } : {}),
    itsm:   ["incidents","changes","cmdb"],
    ai:     ["agents","architecture","intellirag-it","ai-tutor","analytics"],
    config: ["users","billing","apikeys","integrations","audit","notifs","settings"],
  };
  const sectionLabels = { main:"Menu", learn:"Learn", itsm:"ITSM", ai:"AI Engine", config:"Settings & admin" };

  function startNewChat() {
    setChatInitialQuery(null);
    setChatSessionKey((k) => k + 1);
    nav("chat");
    setSideOpen(false);
  }

  return (
    <div className="shell">
      {/* Overlay */}
      {sideOpen && <div className="overlay" onClick={()=>setSideOpen(false)}/>}

      {/* Sidebar */}
      <aside
        className={`sidebar ${sideOpen?"open":""}`}
        style={{ backgroundColor: "#ecece7", color: "#1c1c1c" }}
      >
        <div className="sidebar-logo">
          <div className="logo-mark"><span>W</span></div>
          <span className="logo-text">We<em>crew</em> Copilot</span>
          <span className="logo-badge">v2</span>
          <button className="sidebar-close" onClick={()=>setSideOpen(false)}>✕</button>
        </div>
        <nav className="sidebar-nav">
          {authed && (
            <button type="button" className="sidebar-new-chat" onClick={startNewChat}>
              <span aria-hidden>＋</span> New chat
            </button>
          )}
          {Object.entries(sections).map(([sec,ids])=>(
            <div key={sec}>
              <div className="nav-section">{sectionLabels[sec]}</div>
              {ids.map(id=>{
                const n = NAV_ITEMS.find(x=>x.id===id);
                const badge = id==="notifs"?(unread||null):id==="incidents"?(openIncidents||null):id==="kb"?"6":id==="docs"?"630":null;
                return (
                  <div key={id} className={`nav-item ${page===id?"active":""}`} onClick={()=>nav(id)}>
                    <span className="nav-icon">{n?.icon}</span>
                    <span>{n?.label}</span>
                    {badge&&<span className={`nav-badge ${id==="notifs"&&unread?"red":""}`}>{badge}</span>}
                  </div>
                );
              })}
            </div>
          ))}
        </nav>
        <div className="sidebar-footer" onClick={signOut} title="Sign out" role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && signOut()}>
          <div className="avatar">R</div>
          <div className="flex-1 min-w-0">
            <div className="user-name">Rajkumar Madhu</div>
            <div className="user-role">Signed in</div>
          </div>
          <span className="sidebar-signout-btn">Sign out</span>
        </div>
      </aside>

      {/* Main */}
      <div className={`main ${page === "chat" ? "main-chat" : ""}`}>
        <header className={`topbar ${page === "chat" ? "topbar-chat" : ""}`}>
          <button className="hamburger" onClick={()=>setSideOpen(true)}>☰</button>
          <div className="topbar-title-block">
            <div className="topbar-title">{NAV_ITEMS.find(n=>n.id===page)?.label}</div>
            <div className="topbar-route mono" aria-label="Current route">
              {appPageHash(page).replace(/^#/, "")}
            </div>
          </div>
          <div className="topbar-search">
            <span style={{color:"var(--muted)",fontSize:14}}>🔍</span>
            <input placeholder="Search anything…"/>
            <span className="mono" style={{fontSize:10,background:"var(--border)",padding:"1px 5px",borderRadius:4,color:"var(--muted)"}}>⌘K</span>
          </div>
          <button className="topbar-btn" onClick={()=>nav("notifs")} style={{position:"relative"}}>
            🔔{unread>0&&<span className="notif-dot"/>}
          </button>
          <div className="user-menu-wrap">
            <div
              className="avatar"
              style={{cursor:"pointer",flexShrink:0}}
              onClick={() => setUserMenuOpen((o) => !o)}
              title="Account menu"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && setUserMenuOpen((o) => !o)}
            >
              R
            </div>
            {userMenuOpen && (
              <>
                <div className="overlay" style={{zIndex:250,background:"transparent"}} onClick={() => setUserMenuOpen(false)} />
                <div className="user-menu">
                  <div className="user-menu-head">
                    <div className="user-menu-name">Rajkumar Madhu</div>
                    <div className="user-menu-sub">Wecrew Copilot console</div>
                  </div>
                  <button type="button" className="user-menu-item" onClick={() => { setUserMenuOpen(false); nav("settings"); }}>
                    ⚙ Settings
                  </button>
                  <button type="button" className="user-menu-item danger" onClick={signOut}>
                    ⎋ Sign out
                  </button>
                </div>
              </>
            )}
          </div>
        </header>

        <main className="content">
          <Suspense fallback={<ScreenLoading />}>
            {screens[page] ?? screens.dashboard}
          </Suspense>
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="bottom-nav">
        <div className="bottom-nav-inner">
          {BOTTOM_NAV.map(n=>(
            <div key={n.id} className={`bn-item ${page===n.id?"active":""}`} onClick={()=>nav(n.id)}>
              <span className="bn-icon">{n.icon}</span>
              <span className="bn-label">{n.label}</span>
            </div>
          ))}
        </div>
      </nav>

      {/* Toasts */}
      <div className="toast-wrap">
        {toasts.map(t=>(
          <div key={t.id} className="toast">
            <span>{t.type==="success"?"✅":t.type==="info"?"ℹ️":"⚠️"}</span>
            <span>{t.msg}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
